import { useEffect, useRef, useState } from 'react';
import {
  colorToHex,
  parseColor,
  isAutoLayout,
  isFrameLike,
  STICKY_COLORS,
  toSvg,
  Renderer2D,
  boundsOfNodes,
  exportBounds,
  type Color,
  type Effect,
  type NodeData,
  type NodeId,
  type NodeProps,
  type Paint,
} from '@vellum/core';
import { app } from '../app.ts';
import { safeLink, shaderPreset, shaderPresetInfo, SHADER_PRESETS, type ShaderSource } from '@vellum/core';
import type { ShaderProperties } from '../shaders/runtime.ts';
import { getTextShaper, WIDTH_PROFILES, STRETCH_PRESETS, SCATTER_PRESETS, booleanOperation, generateCode, createFromSpec, linkPreviewSpec, pageFlows, flowStartProps, hasVideo, videoPlaybackOf, componentSetOf, variantOptions, parseVariantName, setVariant, combineAsVariants, variables as allVariables, paragraphFormats, reversePath, sideWeights, bindableVariables, isAutoLayout as autoLayoutOn, type PrototypeAction, type VariableValue } from '@vellum/core';
import { useApp } from '../hooks.ts';
import { Icons } from './icons.tsx';
import { libraryFamilies, uploadFonts } from '../customFonts.ts';
import { discoverLocalFonts, localFamilies, localFontStatus, shareLocalFamily } from '../localFonts.ts';
import { installedPlugins, runPlugin } from '../plugins.ts';
import { widgetMenu } from '../widgets.ts';

type N = NodeData;


/** Layers that can be a pattern tile: the page's top-level layers (and the current source). */
/** Layers made into brushes with Create brush, anywhere in the file. */
function customBrushes(): NodeData[] {
  return [...app.doc.nodes.values()].filter((n) => n.brushDefinition);
}

function patternSources(current?: NodeId): NodeData[] {
  const top = app.doc.children(app.editor.pageId).filter((n) => n.visible);
  const cur = current ? app.doc.get(current) : undefined;
  return cur && !top.includes(cur) ? [cur, ...top] : top;
}

/** Switch an effect to another type, keeping what carries over (color, radius, visibility). */
function effectOfType(t: Effect['type'], x: Effect): Effect {
  const radius = 'radius' in x && typeof x.radius === 'number' ? x.radius : 4;
  const color = 'color' in x && x.color ? x.color : parseColor('#00000040');
  const visible = x.visible;
  switch (t) {
    case 'LAYER_BLUR':
    case 'BACKGROUND_BLUR':
      return { type: t, radius, visible };
    case 'NOISE':
      return { type: 'NOISE', noiseType: 'MONOTONE', color: parseColor('#000000'), size: 1, density: 0.5, opacity: 0.25, visible };
    case 'TEXTURE':
      return { type: 'TEXTURE', size: 4, radius: 4, clipToShape: true, visible };
    case 'SHADER': {
      const p = shaderPreset('pixelate')!;
      return { type: 'SHADER', name: p.name, source: p.source, visible };
    }
    case 'GLASS':
      return { type: 'GLASS', lightIntensity: 0.5, lightAngle: -45, refraction: 0.5, depth: 6, dispersion: 0.2, radius: 4, visible };
    default:
      return { type: t as 'DROP_SHADOW' | 'INNER_SHADOW', color, offset: 'offset' in x ? x.offset : { x: 0, y: 4 }, radius, spread: 0, visible };
  }
}

function same<T>(nodes: N[], f: (n: N) => T): T | 'mixed' {
  if (!nodes.length) return 'mixed';
  const v = f(nodes[0]);
  const k = JSON.stringify(v);
  return nodes.every((n) => JSON.stringify(f(n)) === k) ? v : 'mixed';
}

const update = (props: NodeProps, label = 'Edit properties') => app.editor.update(app.editor.selection, props, label);
const each = (fn: (n: N) => NodeProps, label = 'Edit properties') =>
  app.editor.transact(label, () => {
    for (const n of app.editor.selectedNodes()) app.editor.update(n.id, fn(n), label);
  });

// ---------------------------------------------------------------- inputs

function evalNum(s: string): number | null {
  if (!/^[\d\s+\-*/().]+$/.test(s)) return null;
  try {
    const v = Function(`"use strict";return (${s})`)() as number;
    return Number.isFinite(v) ? v : null;
  } catch {
    return null;
  }
}

export function NumberField({ label, value, onChange, step = 1, min, max, suffix, title, width }: { label?: React.ReactNode; value: number | 'mixed'; onChange: (v: number, live?: boolean) => void; step?: number; min?: number; max?: number; suffix?: string; title?: string; width?: number }) {
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  const shown = value === 'mixed' ? 'Mixed' : String(Math.round(value * 100) / 100);
  const clampV = (v: number) => Math.max(min ?? -Infinity, Math.min(max ?? Infinity, v));
  const scrub = useRef<{ x: number; v: number } | null>(null);
  return (
    <div className="field" title={title} style={width ? { flex: `0 0 ${width}px` } : undefined}>
      {label !== undefined && (
        <label
          onPointerDown={(e) => {
            if (value === 'mixed') return;
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            scrub.current = { x: e.clientX, v: value };
            app.editor.beginGesture('Scrub');
          }}
          onPointerMove={(e) => {
            if (!scrub.current) return;
            const d = Math.round((e.clientX - scrub.current.x) / 2) * step * (e.shiftKey ? 10 : 1);
            onChange(clampV(scrub.current.v + d), true);
          }}
          onPointerUp={() => {
            scrub.current = null;
            app.editor.endGesture();
          }}
        >
          {label}
        </label>
      )}
      <input
        value={focused ? text : shown + (suffix && value !== 'mixed' ? suffix : '')}
        onFocus={(e) => {
          setFocused(true);
          setText(value === 'mixed' ? '' : shown);
          requestAnimationFrame(() => e.target.select());
        }}
        onBlur={() => {
          setFocused(false);
          const v = evalNum(text.replace(suffix ?? '', ''));
          if (v !== null && v !== value) onChange(clampV(v));
        }}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            setText(shown);
            (e.target as HTMLInputElement).blur();
          }
          if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && value !== 'mixed') {
            e.preventDefault();
            const d = (e.key === 'ArrowUp' ? 1 : -1) * step * (e.shiftKey ? 10 : 1);
            const v = clampV((evalNum(text) ?? value) + d);
            setText(String(Math.round(v * 100) / 100));
            onChange(v);
          }
        }}
      />
    </div>
  );
}

function ColorSwatch({ color, onChange }: { color: Color; onChange: (c: Color) => void }) {
  return (
    <span className="swatch">
      <i style={{ background: `rgba(${color.r * 255},${color.g * 255},${color.b * 255},${color.a})` }} />
      <input
        type="color"
        value={colorToHex(color, false)}
        onChange={(e) => {
          const c = parseColor(e.target.value);
          onChange({ ...c, a: color.a });
        }}
      />
    </span>
  );
}

function HexField({ color, onChange }: { color: Color; onChange: (c: Color) => void }) {
  const [t, setT] = useState<string | null>(null);
  const hex = colorToHex(color, false).slice(1).toUpperCase();
  return (
    <input
      style={{ border: 0, background: 'transparent', outline: 'none', width: 60, flex: 'none' }}
      value={t ?? hex}
      onFocus={(e) => {
        setT(hex);
        requestAnimationFrame(() => e.target.select());
      }}
      onChange={(e) => setT(e.target.value)}
      onBlur={() => {
        if (t !== null) {
          try {
            const c = parseColor(t.startsWith('#') ? t : `#${t}`);
            onChange({ ...c, a: t.replace('#', '').length >= 8 ? c.a : color.a });
          } catch {
            /* ignore */
          }
        }
        setT(null);
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

function Seg<T extends string>({ value, options, onChange }: { value: T | 'mixed'; options: { v: T; label: React.ReactNode; title?: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="seg">
      {options.map((o) => (
        <button key={o.v} title={o.title} className={value === o.v ? 'on' : ''} onClick={() => onChange(o.v)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Select<T extends string>({ value, options, onChange, label }: { value: T | 'mixed'; options: { v: T; label: string; group?: string }[]; onChange: (v: T) => void; label?: string }) {
  // consecutive options with the same group go under one heading
  const runs: { group?: string; items: typeof options }[] = [];
  for (const o of options) {
    const last = runs[runs.length - 1];
    if (last && last.group === o.group) last.items.push(o);
    else runs.push({ group: o.group, items: [o] });
  }
  const opt = (o: (typeof options)[number]) => (
    <option key={o.v} value={o.v}>
      {o.label}
    </option>
  );
  return (
    <div className="field">
      {label && <label>{label}</label>}
      <select value={value === 'mixed' ? '' : value} onChange={(e) => onChange(e.target.value as T)}>
        {value === 'mixed' && <option value="">Mixed</option>}
        {runs.map((r, i) =>
          r.group ? (
            <optgroup key={`${r.group}${i}`} label={r.group}>
              {r.items.map(opt)}
            </optgroup>
          ) : (
            r.items.map(opt)
          ),
        )}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------- panel

export function PropertiesPanel() {
  useApp(['doc', 'selection', 'tool'], () => app.docVersion);
  const nodes = app.editor.selectedNodes();
  return (
    <div className={`panel right${app.readOnly ? ' read-only' : ''}`} onPointerDown={(e) => e.stopPropagation()}>
      {app.tool === 'frame' && nodes.length === 0 ? <FramePresets /> : nodes.length === 0 ? <PageProps /> : <NodeProps nodes={nodes} />}
    </div>
  );
}

const FRAME_PRESETS: [string, [string, number, number][]][] = [
  ['Desktop', [['Desktop', 1440, 1024], ['MacBook Air', 1280, 832], ['Wide', 1920, 1080]]],
  ['Phone', [['iPhone 16', 393, 852], ['iPhone 16 Pro Max', 440, 956], ['Android', 412, 917]]],
  ['Tablet', [['iPad', 834, 1194], ['iPad Pro', 1024, 1366]]],
  ['Social', [['Square post', 1080, 1080], ['Story', 1080, 1920], ['Open Graph', 1200, 630]]],
  ['Slides', [['16:9', 1920, 1080], ['4:3', 1024, 768]]],
];

function FramePresets() {
  return (
    <div className="prop-section">
      <h4>Frame presets</h4>
      {FRAME_PRESETS.map(([group, items]) => (
        <div key={group} style={{ marginBottom: 8 }}>
          <div style={{ color: 'var(--text-2)', marginBottom: 2 }}>{group}</div>
          {items.map(([name, w, h]) => (
            <button
              key={name}
              className="asset-row"
              style={{ width: '100%', padding: '0 4px' }}
              onClick={() => {
                const c = app.screenToPage({ x: app.viewportSize.width / 2, y: app.viewportSize.height / 2 });
                // place to the right of existing content if the center is occupied
                const id = app.editor.create('FRAME', { name, x: Math.round(c.x - w / 2), y: Math.round(c.y - h / 2), width: w, height: h });
                app.editor.setSelection([id]);
                app.setTool('select');
                app.zoomToNodes([id]);
              }}
            >
              <span className="name">{name}</span>
              <span className="meta">
                {w}×{h}
              </span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

function GridSection({ node }: { node: N }) {
  const grids = node.layoutGrids ?? [];
  const set = (g: NonNullable<N['layoutGrids']>) => app.editor.update(node.id, { layoutGrids: g.length ? g : undefined }, 'Layout grid');
  return (
    <div className="prop-section">
      <h4>
        <span>Layout grid</span>
        <span style={{ display: 'flex', gap: 2 }}>
          {grids.length > 0 && !node.gridStyleId && (
            <button
              title="Create a grid style from these grids"
              onClick={() => {
                const name = prompt('Grid style name', `${grids[0].count ?? grids[0].size ?? ''} ${grids[0].pattern.toLowerCase()}`.trim());
                if (name) app.editor.applyStyle([node.id], app.editor.createStyle('GRID', name, { layoutGrids: structuredClone(grids) }), 'grid');
              }}
            >
              ◈
            </button>
          )}
          <button title="Add layout grid" onClick={() => set([...grids, grids.length ? { pattern: 'GRID', size: 8 } : { pattern: 'COLUMNS', count: 12, gutter: 20, margin: 24 }])}>
            {Icons.plus}
          </button>
        </span>
      </h4>
      <StyleChip nodes={[node]} kind="gridStyleId" />
      {grids.map((g, i) => {
        const upd = (p: Partial<(typeof grids)[number]>) => set(grids.map((x, j) => (j === i ? { ...x, ...p } : x)));
        return (
          <div key={i} style={{ marginBottom: 6 }}>
            <div className="row">
              <Select
                value={g.pattern}
                options={[
                  { v: 'COLUMNS', label: 'Columns' },
                  { v: 'ROWS', label: 'Rows' },
                  { v: 'GRID', label: 'Grid' },
                ]}
                onChange={(v) => upd({ pattern: v })}
              />
              <button className="icon-btn" onClick={() => upd({ visible: g.visible === false ? undefined : false })}>
                {g.visible === false ? Icons.eyeOff : Icons.eye}
              </button>
              <button className="icon-btn" onClick={() => set(grids.filter((_, j) => j !== i))}>
                {Icons.minus}
              </button>
            </div>
            {g.pattern === 'GRID' ? (
              <div className="row">
                <NumberField label="Size" min={1} value={g.size ?? 8} onChange={(v) => upd({ size: v })} />
              </div>
            ) : (
              <div className="row">
                <NumberField label="#" min={1} value={g.count ?? 12} onChange={(v) => upd({ count: v })} />
                <NumberField label="Gut" min={0} value={g.gutter ?? 20} onChange={(v) => upd({ gutter: v })} />
                <NumberField label="Mar" min={0} value={g.margin ?? 0} onChange={(v) => upd({ margin: v })} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PageProps() {
  const page = app.doc.get(app.editor.pageId);
  if (!page) return null;
  const bg = page.backgroundColor ?? { r: 0.96, g: 0.96, b: 0.96, a: 1 };
  return (
    <>
      <div className="prop-section">
        <h4>Page</h4>
        <div className="paint-row">
          <div className="field">
            <ColorSwatch color={bg} onChange={(c) => app.editor.update(page.id, { backgroundColor: c }, 'Page background')} />
            <HexField color={bg} onChange={(c) => app.editor.update(page.id, { backgroundColor: c }, 'Page background')} />
          </div>
        </div>
        <div className="row">
          <Seg
            value={page.pageKind ?? 'DESIGN'}
            options={[
              { v: 'DESIGN', label: 'Design' },
              { v: 'WHITEBOARD', label: 'Whiteboard' },
            ]}
            onChange={(v) => app.editor.update(page.id, { pageKind: v }, 'Page type')}
          />
        </div>
      </div>
      <div className="empty-hint">
        Select a layer to edit its properties.
        <br />
        <br />
        <b>Tip:</b> agents connected over MCP edit this page live. Click <span className="chip">{Icons.sparkle} Connect AI</span> to set one up.
      </div>
      <ExportSection nodes={[]} />
    </>
  );
}

function NodeProps({ nodes }: { nodes: N[] }) {
  const one = nodes.length === 1 ? nodes[0] : null;
  const types = new Set(nodes.map((n) => n.type));
  const anyText = nodes.some((n) => n.characters !== undefined && n.type !== 'CONNECTOR');
  const frames = nodes.filter((n) => isFrameLike(n.type));
  const parent = one ? app.doc.get(one.parent) : null;
  const inAuto = nodes.some((n) => isAutoLayout(app.doc.get(n.parent)) && n.layoutPositioning !== 'ABSOLUTE');
  const connector = types.size === 1 && types.has('CONNECTOR');
  return (
    <>
      <Header nodes={nodes} />
      {nodes.length > 1 && <AlignSection />}
      {!connector && <GeometrySection nodes={nodes} />}
      {(frames.length > 0 || inAuto) && <AutoLayoutSection nodes={nodes} frames={frames} inAuto={inAuto} />}
      {one && parent && isFrameLike(parent.type) && !isAutoLayout(parent) && <ConstraintsSection node={one} />}
      <LayerSection nodes={nodes} />
      <ModesSection nodes={nodes} />
      <BoundChips nodes={nodes} />
      {types.has('STICKY') && <StickySection nodes={nodes.filter((n) => n.type === 'STICKY')} />}
      {types.has('SHAPE_WITH_TEXT') && <ShapeSection nodes={nodes.filter((n) => n.type === 'SHAPE_WITH_TEXT')} />}
      {connector && <ConnectorSection nodes={nodes} />}
      {anyText && <TextSection nodes={nodes.filter((n) => n.characters !== undefined)} />}
      {!connector && !types.has('GROUP') && <PaintsSection nodes={nodes} kind="fills" title={types.size === 1 && types.has('TEXT') ? 'Fill' : 'Fill'} />}
      {!types.has('GROUP') && <PaintsSection nodes={nodes} kind="strokes" title="Stroke" />}
      {!types.has('GROUP') && <EffectsSection nodes={nodes} />}
      {nodes.length === 1 && isFrameLike(nodes[0].type) && <GridSection node={nodes[0]} />}
      {nodes.length === 1 && <PrototypeSection node={nodes[0]} />}
      <ExportSection nodes={nodes} />
      <CodeSection nodes={nodes} />
    </>
  );
}

const MEDIA_ACTIONS = [
  ['TOGGLE_PLAY_PAUSE', 'Play / pause'],
  ['PLAY', 'Play'],
  ['PAUSE', 'Pause'],
  ['TOGGLE_MUTE_UNMUTE', 'Mute / unmute'],
  ['MUTE', 'Mute'],
  ['UNMUTE', 'Unmute'],
  ['SKIP_FORWARD', 'Skip forward'],
  ['SKIP_BACKWARD', 'Skip backward'],
  ['SKIP_TO', 'Skip to'],
] as const;

const TRANSITIONS = ['INSTANT', 'DISSOLVE', 'SLIDE_LEFT', 'SLIDE_RIGHT', 'SLIDE_UP', 'SLIDE_DOWN', 'PUSH_LEFT', 'PUSH_RIGHT', 'SMART_ANIMATE'] as const;

function PrototypeSection({ node }: { node: N }) {
  const reactions = node.reactions ?? [];
  const frames = app.doc.children(app.editor.pageId).filter((n) => n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE');
  const set = (r: NonNullable<N['reactions']>) => app.editor.update(node.id, { reactions: r.length ? r : undefined }, 'Edit interaction');
  const page = app.doc.get(app.editor.pageId)!;
  const isTopFrame = frames.some((f) => f.id === node.id);
  const flow = pageFlows(app.doc, page.id).find((f) => f.nodeId === node.id);
  const isVideo = hasVideo(node);
  // video layers on the screen this layer belongs to, for "Control video"
  const screen = [node, ...app.doc.ancestors(node.id)].find((n) => app.doc.get(n.parent)?.type === 'PAGE');
  const videos: N[] = [];
  if (screen) app.doc.walk(screen.id, (n) => void (hasVideo(n) && videos.push(n)));
  const playback = videoPlaybackOf(node);
  return (
    <div className="prop-section">
      <h4>
        <span>Prototype</span>
        <button
          title="Add interaction"
          onClick={() => {
            const target = frames.find((f) => f.id !== node.id && !app.doc.isAncestor(f.id, node.id));
            // inside a variant, the useful default is switching to the next variant
            const vs = variantsFor(node);
            const own = [node, ...app.doc.ancestors(node.id)].find((n) => n.type === 'COMPONENT' && vs.some((v) => v.id === n.id));
            const next = own ? vs[(vs.findIndex((v) => v.id === own.id) + 1) % vs.length] : undefined;
            if (next && next.id !== own!.id) return set([...reactions, { trigger: 'ON_CLICK', action: { type: 'CHANGE_TO', destinationId: next.id } }]);
            set([...reactions, target ? { trigger: 'ON_CLICK', action: { type: 'NAVIGATE', destinationId: target.id, transition: 'DISSOLVE', duration: 300 } } : { trigger: 'ON_CLICK', action: { type: 'BACK' } }]);
          }}
        >
          {Icons.plus}
        </button>
      </h4>
      {(node.type === 'FRAME' || node.type === 'COMPONENT' || node.type === 'INSTANCE') && (
        <div className="row">
          <Select
            label="Scrolling"
            value={node.overflowScroll ?? 'NONE'}
            options={[
              { v: 'NONE', label: 'No scrolling' },
              { v: 'VERTICAL', label: 'Vertical' },
              { v: 'HORIZONTAL', label: 'Horizontal' },
              { v: 'BOTH', label: 'Both directions' },
            ]}
            onChange={(v) => app.editor.update(node.id, { overflowScroll: v === 'NONE' ? undefined : v, ...(v !== 'NONE' ? { clipsContent: true } : {}) }, 'Scrolling')}
          />
        </div>
      )}
      {app.doc.get(node.parent)?.overflowScroll && (
        <div className="row">
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--text-2)' }}>
            <input type="checkbox" checked={!!node.scrollFixed} onChange={(e) => app.editor.update(node.id, { scrollFixed: e.target.checked || undefined }, 'Fix position')} /> Fix position when scrolling
          </label>
        </div>
      )}
      {isVideo && (
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', color: 'var(--text-2)' }}>
          {(['autoplay', 'loop', 'muted', 'showControls'] as const).map((k) => (
            <label key={k} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={playback[k]} onChange={(e) => app.editor.update(node.id, { videoPlayback: { ...node.videoPlayback, [k]: e.target.checked } }, 'Video playback')} /> {k === 'autoplay' ? 'Autoplay' : k === 'loop' ? 'Loop' : k === 'muted' ? 'Mute' : 'Controls'}
            </label>
          ))}
        </div>
      )}
      {isTopFrame && (
        <div className="row">
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--text-2)' }}>
            <input type="checkbox" checked={!!flow} onChange={(e) => app.editor.update(page.id, flowStartProps(app.doc, page.id, node.id, e.target.checked ? '' : null), e.target.checked ? 'Add flow' : 'Remove flow')} /> Flow starting point
          </label>
          {flow && <input key={flow.name} defaultValue={flow.name} title="Flow name" style={{ flex: 1, minWidth: 0 }} onBlur={(e) => e.target.value !== flow.name && app.editor.update(page.id, flowStartProps(app.doc, page.id, node.id, e.target.value), 'Rename flow')} onKeyDown={(e) => (e.stopPropagation(), e.key === 'Enter' && (e.target as HTMLInputElement).blur())} />}
        </div>
      )}
      {reactions.map((r, i) => {
        const a = r.action;
        const upd = (nr: (typeof reactions)[number]) => set(reactions.map((x, j) => (j === i ? nr : x)));
        return (
          <div key={i} style={{ marginBottom: 8 }}>
            <div className="row">
              <Select
                value={r.trigger}
                options={[
                  { v: 'ON_CLICK', label: 'On click' },
                  { v: 'ON_HOVER', label: 'While hovering' },
                  { v: 'ON_PRESS', label: 'While pressing' },
                  { v: 'AFTER_DELAY', label: 'After delay' },
                  ...(isVideo || r.trigger === 'ON_MEDIA_END' || r.trigger === 'ON_MEDIA_HIT'
                    ? [
                        { v: 'ON_MEDIA_END' as const, label: 'Video ends' },
                        { v: 'ON_MEDIA_HIT' as const, label: 'Video reaches' },
                      ]
                    : []),
                ]}
                onChange={(t) => upd({ ...r, trigger: t, delay: t === 'AFTER_DELAY' ? 800 : undefined, mediaHitTime: t === 'ON_MEDIA_HIT' ? 1 : undefined })}
              />
              <button className="icon-btn" onClick={() => set(reactions.filter((_, j) => j !== i))}>
                {Icons.minus}
              </button>
            </div>
            <div className="row">
              <Select
                value={a.type}
                options={[
                  { v: 'NAVIGATE', label: 'Navigate to' },
                  { v: 'OVERLAY', label: 'Open overlay' },
                  { v: 'BACK', label: 'Back' },
                  { v: 'CLOSE_OVERLAY', label: 'Close overlay' },
                  { v: 'OPEN_URL', label: 'Open link' },
                  ...(variantsFor(node).length ? [{ v: 'CHANGE_TO' as const, label: 'Change to' }] : []),
                  { v: 'SET_VARIABLE', label: 'Set variable' },
                  ...(videos.length ? [{ v: 'UPDATE_MEDIA_RUNTIME' as const, label: 'Control video' }] : []),
                  ...(a.type === 'CONDITIONAL' ? [{ v: 'CONDITIONAL' as const, label: 'Conditional' }] : []),
                ]}
                onChange={(t) => {
                  const vars = allVariables(app.doc);
                  const action: PrototypeAction =
                    t === 'NAVIGATE' || t === 'OVERLAY'
                      ? { type: t, destinationId: frames[0]?.id ?? '', transition: 'DISSOLVE', duration: 300 }
                      : t === 'OPEN_URL'
                        ? { type: 'OPEN_URL', url: 'https://' }
                        : t === 'CHANGE_TO'
                          ? { type: 'CHANGE_TO', destinationId: variantsFor(node).find((v) => v.id !== node.componentId)?.id ?? '' }
                          : t === 'SET_VARIABLE'
                            ? vars.length
                              ? { type: 'SET_VARIABLE', variableId: vars[0].id, ...(vars[0].resolvedType === 'BOOLEAN' ? { toggle: true } : { value: vars[0].resolvedType === 'FLOAT' ? 0 : '' }) }
                              : (app.toast('Create a variable first (Assets → Variables)'), a)
                            : t === 'CONDITIONAL'
                              ? a
                              : t === 'UPDATE_MEDIA_RUNTIME'
                                ? { type: 'UPDATE_MEDIA_RUNTIME', destinationId: isVideo ? null : (videos[0]?.id ?? null), mediaAction: 'TOGGLE_PLAY_PAUSE' }
                                : { type: t };
                  upd({ ...r, action });
                }}
              />
            </div>
            {a.type === 'CHANGE_TO' && (
              <div className="row">
                <Select value={a.destinationId} options={variantsFor(node).map((v) => ({ v: v.id, label: v.name }))} onChange={(d) => upd({ ...r, action: { type: 'CHANGE_TO', destinationId: d } })} />
              </div>
            )}
            {a.type === 'UPDATE_MEDIA_RUNTIME' && (
              <div className="row" style={{ flexWrap: 'wrap' }}>
                <Select value={a.destinationId ?? ''} options={[...(isVideo ? [{ v: '', label: 'This video' }] : []), ...videos.filter((v) => v.id !== node.id || !isVideo).map((v) => ({ v: v.id, label: v.name }))]} onChange={(d) => upd({ ...r, action: { ...a, destinationId: d || null } })} />
                <Select
                  value={a.mediaAction}
                  options={MEDIA_ACTIONS.map(([v, label]) => ({ v, label }))}
                  onChange={(m) => upd({ ...r, action: { type: 'UPDATE_MEDIA_RUNTIME', destinationId: a.destinationId, mediaAction: m, ...(m === 'SKIP_FORWARD' || m === 'SKIP_BACKWARD' ? { amountToSkip: 5 } : m === 'SKIP_TO' ? { newTimestamp: 0 } : {}) } })}
                />
                {(a.mediaAction === 'SKIP_FORWARD' || a.mediaAction === 'SKIP_BACKWARD') && <NumberField label="s" value={a.amountToSkip ?? 5} min={0} onChange={(v) => upd({ ...r, action: { ...a, amountToSkip: v } })} />}
                {a.mediaAction === 'SKIP_TO' && <NumberField label="at s" value={a.newTimestamp ?? 0} min={0} onChange={(v) => upd({ ...r, action: { ...a, newTimestamp: v } })} />}
              </div>
            )}
            {a.type === 'SET_VARIABLE' && <SetVariableRow action={a} onChange={(na) => upd({ ...r, action: na })} />}
            {a.type === 'CONDITIONAL' && (
              <div className="row" style={{ color: 'var(--text-2)' }}>
                If {app.doc.get(a.variableId)?.name ?? '?'} = {JSON.stringify(a.equals)} → {a.then.map((x) => x.type.toLowerCase()).join(', ') || 'nothing'}
                {a.else?.length ? ` · else ${a.else.map((x) => x.type.toLowerCase()).join(', ')}` : ''}
              </div>
            )}
            {(a.type === 'NAVIGATE' || a.type === 'OVERLAY') && (
              <>
                <div className="row">
                  <Select value={a.destinationId} options={frames.map((f) => ({ v: f.id, label: f.name }))} onChange={(d) => upd({ ...r, action: { ...a, destinationId: d } })} />
                </div>
                <div className="row">
                  <Select value={a.transition ?? 'INSTANT'} options={TRANSITIONS.map((t) => ({ v: t, label: t.charAt(0) + t.slice(1).toLowerCase().replace('_', ' ') }))} onChange={(t) => upd({ ...r, action: { ...a, transition: t } })} />
                  <NumberField label="ms" value={a.duration ?? 300} min={0} onChange={(v) => upd({ ...r, action: { ...a, duration: v } })} />
                </div>
              </>
            )}
            {a.type === 'OPEN_URL' && (
              <div className="row">
                <div className="field">
                  <input defaultValue={a.url} onBlur={(e) => upd({ ...r, action: { type: 'OPEN_URL', url: e.target.value } })} onKeyDown={(e) => e.stopPropagation()} />
                </div>
              </div>
            )}
            {r.trigger === 'ON_MEDIA_HIT' && (
              <div className="row">
                <NumberField label="at s" value={r.mediaHitTime ?? 1} min={0} onChange={(v) => upd({ ...r, mediaHitTime: v })} />
              </div>
            )}
            {r.trigger === 'AFTER_DELAY' && (
              <div className="row">
                <NumberField label="delay" value={r.delay ?? 800} min={0} onChange={(v) => upd({ ...r, delay: v })} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Re-read a link card's page and rebuild the card in place (same position and parent). */
async function refreshLinkPreview(n: N) {
  const r = await fetch('/api/unfurl', { method: 'POST', body: JSON.stringify({ url: n.linkPreview!.url }), headers: { 'content-type': 'application/json' } });
  const meta = (await r.json()) as N['linkPreview'] & { error?: string };
  if (!r.ok || !meta) return app.toast(`Couldn't refresh the preview: ${meta?.error ?? r.statusText}`);
  app.editor.transact('Refresh link preview', () => {
    const index = app.doc.children(n.parent!).findIndex((c) => c.id === n.id);
    const made = createFromSpec(app.editor, linkPreviewSpec(meta, { x: n.x, y: n.y }), n.parent!, {}, { index });
    app.editor.remove([n.id]);
    app.editor.setSelection(made.ids);
  });
}

function Header({ nodes }: { nodes: N[] }) {
  const one = nodes.length === 1 ? nodes[0] : null;
  const comp = one && (one.type === 'COMPONENT' || one.type === 'INSTANCE');
  return (
    <div className="prop-section" style={{ paddingTop: 12 }}>
      <h4 style={{ color: comp ? 'var(--component)' : undefined }}>
        <span>{one ? `${prettyType(one)} · ${one.name}` : `${nodes.length} layers`}</span>
      </h4>
      {one?.type === 'INSTANCE' && one.componentId && componentSetOf(app.doc, one.componentId) && (
        <VariantPicker instance={one} />
      )}
      {one?.type === 'INSTANCE' && <InstanceProps instance={one} />}
      {one?.type === 'INSTANCE' && <NestedInstances instance={one} />}
      {one?.relaunchData && <RelaunchButtons node={one} />}
      {one?.linkPreview && (
        <div className="row" style={{ gap: 6 }}>
          <button className="btn secondary" title={one.linkPreview.url} onClick={() => {
              const link = safeLink(one.linkPreview!.url);
              if (link) window.open(link, '_blank', 'noopener');
            }}>
            ↗ Open link
          </button>
          <button className="btn secondary" title="Read the page again" onClick={() => void refreshLinkPreview(one)}>
            ⟳ Refresh
          </button>
        </div>
      )}
      {one?.widget && (
        <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
          <span className="chip">⚙ Widget · {one.widget.name}</span>
          {(one.widget.menu ?? []).map((m) => (
            <button key={m.propertyName} className="btn secondary" onClick={() => widgetMenu(one.id, m.propertyName)}>
              {m.tooltip}
            </button>
          ))}
        </div>
      )}
      {one?.type === 'COMPONENT' && one.componentProps && Object.keys(one.componentProps).length > 0 && (
        <div style={{ marginBottom: 6 }}>
          {Object.entries(one.componentProps).map(([k, d]) => (
            <div className="row" key={k} style={{ flexWrap: 'wrap' }}>
              <span className="chip" style={{ flex: 1 }}>
                {d.type === 'BOOLEAN' ? '◐' : d.type === 'INSTANCE_SWAP' ? '⇄' : 'T'} {k}
              </span>
              <span style={{ color: 'var(--text-3)' }}>{(d.type === 'INSTANCE_SWAP' ? (app.doc.get(String(d.default))?.name ?? '') : String(d.default)).slice(0, 14)}</span>
              {d.type === 'INSTANCE_SWAP' && <PreferredValues component={one} prop={k} />}
            </div>
          ))}
        </div>
      )}
      {one && one.type !== 'COMPONENT' && app.doc.ancestors(one.id).some((a) => a.type === 'COMPONENT') && !app.doc.ancestors(one.id).some((a) => a.type === 'INSTANCE') && (
        <div className="row">
          <button className="btn secondary" title="Instances get a toggle for this layer" onClick={() => app.editor.addComponentProperty(one.id, 'visible')}>
            ◐ Expose visibility
          </button>
          {one.type === 'INSTANCE' && (
            <button className="btn secondary" title="Instances can swap this nested component" onClick={() => app.editor.addComponentProperty(one.id, 'componentId')}>
              ⇄ Expose swap
            </button>
          )}
          {one.type === 'INSTANCE' && (
            <button className={`btn secondary ${one.exposedInstance ? 'on' : ''}`} title="Show this instance's properties on instances of the parent component" onClick={() => app.editor.update(one.id, { exposedInstance: one.exposedInstance ? undefined : true }, 'Expose nested properties')}>
              ↗ {one.exposedInstance ? 'Exposed' : 'Expose properties'}
            </button>
          )}
          {one.type === 'TEXT' && (
            <button className="btn secondary" title="Instances can set this text" onClick={() => app.editor.addComponentProperty(one.id, 'characters')}>
              T Expose text
            </button>
          )}
        </div>
      )}
      {nodes.length > 1 && nodes.every((n) => n.type === 'COMPONENT') && (
        <div className="row">
          <button className="btn secondary" onClick={() => app.editor.setSelection([combineAsVariants(app.editor, app.editor.selection)])}>
            {Icons.component} Combine as variants
          </button>
        </div>
      )}
      {one?.type === 'INSTANCE' && (
        <div className="row">
          <button className="btn secondary" onClick={() => one.componentId && app.doc.has(one.componentId) && (app.editor.setPage(app.doc.pageOf(one.componentId)!.id), app.editor.setSelection([one.componentId]), app.zoomToNodes([one.componentId]))}>
            Go to main
          </button>
          <button className="btn secondary" onClick={() => app.editor.resetOverrides(one.id)}>
            Reset
          </button>
          <button className="btn secondary" onClick={() => app.editor.detachInstance(one.id)}>
            Detach
          </button>
        </div>
      )}
      {one?.type === 'COMPONENT' && (
        <div className="row">
          <button
            className="btn secondary"
            onClick={() => {
              const b = app.doc.worldBounds(one.id);
              app.editor.createInstance(one.id, { x: b.x + b.width + 40, y: b.y });
            }}
          >
            Create instance
          </button>
        </div>
      )}
      {nodes.length > 1 && nodes.every((n) => !['TEXT', 'CONNECTOR', 'STICKY', 'SECTION'].includes(n.type)) && (
        <div className="row">
          {(['UNION', 'SUBTRACT', 'INTERSECT', 'EXCLUDE'] as const).map((k) => (
            <button key={k} className="btn secondary" style={{ padding: '0 8px' }} title={k.toLowerCase()} onClick={() => booleanOperation(app.editor, app.editor.selection, k)}>
              {{ UNION: '⊕', SUBTRACT: '⊖', INTERSECT: '⊗', EXCLUDE: '⊘' }[k]} {k.charAt(0) + k.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      )}
      {nodes.some((n) => n.type === 'FRAME' || n.type === 'GROUP') && nodes.length >= 1 && one?.type !== 'COMPONENT' && one?.type !== 'INSTANCE' && (
        <div className="row">
          <button className="btn secondary" onClick={() => app.editor.createComponent(app.editor.selection)}>
            {Icons.component} Create component
          </button>
        </div>
      )}
    </div>
  );
}

/** Variants an interaction on this layer can switch its component to. */
function variantsFor(node: N): N[] {
  for (const n of [node, ...app.doc.ancestors(node.id)]) {
    const cid = n.type === 'INSTANCE' ? n.componentId : n.type === 'COMPONENT' ? n.id : undefined;
    const set = cid ? componentSetOf(app.doc, cid) : undefined;
    if (set) return app.doc.children(set.id).filter((c) => c.type === 'COMPONENT');
  }
  return [];
}

function SetVariableRow({ action, onChange }: { action: Extract<PrototypeAction, { type: 'SET_VARIABLE' }>; onChange: (a: PrototypeAction) => void }) {
  const vars = allVariables(app.doc);
  const v = app.doc.get(action.variableId);
  const kind = v?.resolvedType ?? 'STRING';
  const mode = action.toggle ? 'toggle' : action.add !== undefined ? 'add' : 'set';
  const shown = action.add ?? action.value;
  const parse = (s: string): VariableValue => (kind === 'FLOAT' ? Number(s) || 0 : kind === 'BOOLEAN' ? s === 'true' : kind === 'COLOR' ? parseColor(s) : s);
  return (
    <>
      <div className="row">
        <Select
          value={action.variableId}
          options={vars.map((x) => ({ v: x.id, label: x.name }))}
          onChange={(id) => {
            const t = app.doc.get(id)?.resolvedType;
            onChange({ type: 'SET_VARIABLE', variableId: id, ...(t === 'BOOLEAN' ? { toggle: true } : { value: t === 'FLOAT' ? 0 : t === 'COLOR' ? parseColor('#000000') : '' }) });
          }}
        />
      </div>
      <div className="row">
        <Select
          value={mode}
          options={[
            { v: 'set' as const, label: 'Set to' },
            ...(kind === 'BOOLEAN' ? [{ v: 'toggle' as const, label: 'Toggle' }] : []),
            ...(kind === 'FLOAT' ? [{ v: 'add' as const, label: 'Add' }] : []),
          ]}
          onChange={(m) => onChange({ type: 'SET_VARIABLE', variableId: action.variableId, ...(m === 'toggle' ? { toggle: true } : m === 'add' ? { add: 1 } : { value: kind === 'BOOLEAN' ? true : kind === 'FLOAT' ? 0 : '' }) })}
        />
        {mode !== 'toggle' && (
          <div className="field">
            <input
              key={JSON.stringify(shown)}
              defaultValue={shown === undefined ? '' : typeof shown === 'object' && shown && 'r' in shown ? colorToHex(shown as Color) : String(shown)}
              onBlur={(e) => onChange(mode === 'add' ? { ...action, add: Number(e.target.value) || 0 } : { type: 'SET_VARIABLE', variableId: action.variableId, value: parse(e.target.value) })}
              onKeyDown={(e) => (e.stopPropagation(), e.key === 'Enter' && (e.target as HTMLInputElement).blur())}
            />
          </div>
        )}
      </div>
    </>
  );
}

function InstanceProps({ instance }: { instance: N }) {
  const comp = instance.componentId ? app.doc.get(instance.componentId) : null;
  const defs = comp?.componentProps ?? {};
  if (!Object.keys(defs).length) return null;
  return (
    <div style={{ marginBottom: 6 }}>
      {Object.entries(defs).map(([k, d]) => {
        const v = instance.propValues?.[k] ?? d.default;
        return (
          <div className="row" key={k}>
            <span style={{ width: 80, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={k}>
              {k}
            </span>
            {d.type === 'BOOLEAN' ? (
              <input type="checkbox" checked={!!v} onChange={(e) => app.editor.setInstanceProperties(instance.id, { [k]: e.target.checked })} />
            ) : d.type === 'INSTANCE_SWAP' ? (
              <Select value={String(v)} options={swapOptions(String(v), d.preferred)} onChange={(id) => app.editor.setInstanceProperties(instance.id, { [k]: id })} />
            ) : (
              <div className="field">
                <input key={String(v)} defaultValue={String(v)} onBlur={(e) => e.target.value !== v && app.editor.setInstanceProperties(instance.id, { [k]: e.target.value })} onKeyDown={(e) => (e.stopPropagation(), e.key === 'Enter' && (e.target as HTMLInputElement).blur())} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Properties of nested instances the component exposes, editable right on this instance. */
function NestedInstances({ instance }: { instance: N }) {
  const nested: N[] = [];
  const walk = (id: string) => {
    for (const c of app.doc.children(id)) {
      if (c.type === 'INSTANCE' && c.exposedInstance) nested.push(c);
      // exposed instances can expose their own nested instances in turn
      if (c.type !== 'INSTANCE' || c.exposedInstance) walk(c.id);
    }
  };
  walk(instance.id);
  if (!nested.length) return null;
  return (
    <div className="nested-instances">
      {nested.map((n) => (
        <div key={n.id}>
          <div className="nested-title">↳ {n.name}</div>
          {componentSetOf(app.doc, n.componentId!) && <VariantPicker instance={n} />}
          <InstanceProps instance={n} />
        </div>
      ))}
    </div>
  );
}

/** Components an instance-swap property can point at: preferred ones first, then the rest. */
function swapOptions(current: string, preferred?: string[]) {
  const comps = [...app.doc.nodes.values()].filter((n) => n.type === 'COMPONENT');
  if (current && !comps.some((c) => c.id === current)) {
    const c = app.doc.get(current);
    if (c) comps.unshift(c);
  }
  const pref = (preferred ?? []).map((id) => app.doc.get(id)).filter((c): c is N => !!c);
  if (!pref.length) return comps.map((c) => ({ v: c.id, label: c.name }));
  const rest = comps.filter((c) => !pref.some((p) => p.id === c.id));
  return [...pref.map((c) => ({ v: c.id, label: `★ ${c.name}` })), ...rest.map((c) => ({ v: c.id, label: c.name }))];
}

/** Pick which components an instance-swap property suggests first. */
function PreferredValues({ component, prop }: { component: N; prop: string }) {
  const [open, setOpen] = useState(false);
  const def = component.componentProps![prop];
  const chosen = new Set(def.preferred ?? []);
  const comps = [...app.doc.nodes.values()].filter((n) => n.type === 'COMPONENT' && n.id !== component.id);
  return (
    <>
      <button className="icon-btn" title="Preferred values" onClick={() => setOpen(!open)}>
        ★{chosen.size ? chosen.size : ''}
      </button>
      {open && (
        <div className="preferred-list">
          {comps.map((c) => (
            <label key={c.id}>
              <input
                type="checkbox"
                checked={chosen.has(c.id)}
                onChange={(e) => {
                  const next = new Set(chosen);
                  if (e.target.checked) next.add(c.id);
                  else next.delete(c.id);
                  app.editor.setPreferredValues(component.id, prop, [...next]);
                }}
              />{' '}
              {c.name}
            </label>
          ))}
        </div>
      )}
    </>
  );
}

function VariantPicker({ instance }: { instance: N }) {
  const set = componentSetOf(app.doc, instance.componentId!)!;
  const opts = variantOptions(app.doc, set.id);
  const current = parseVariantName(app.doc.get(instance.componentId!)?.name ?? '');
  return (
    <div style={{ marginBottom: 6 }}>
      {Object.entries(opts).map(([prop, values]) => (
        <div className="row" key={prop}>
          <span style={{ width: 70, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{prop}</span>
          <Select value={current[prop] ?? values[0]} options={values.map((v) => ({ v, label: v }))} onChange={(v) => setVariant(app.editor, instance.id, { [prop]: v })} />
        </div>
      ))}
    </div>
  );
}

function prettyType(n: N) {
  if (n.isComponentSet) return 'Component set';
  const map: Partial<Record<N['type'], string>> = { SHAPE_WITH_TEXT: 'Shape', RECTANGLE: 'Rectangle', STICKY: 'Sticky' };
  return map[n.type] ?? n.type.charAt(0) + n.type.slice(1).toLowerCase();
}

function AlignSection() {
  const a = (m: Parameters<typeof app.editor.align>[1]) => app.editor.align(app.editor.selection, m);
  return (
    <div className="prop-section">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <button className="icon-btn" title="Align left" onClick={() => a('LEFT')}>{Icons.alignL}</button>
        <button className="icon-btn" title="Align center" onClick={() => a('CENTER_H')}>{Icons.alignCH}</button>
        <button className="icon-btn" title="Align right" onClick={() => a('RIGHT')}>{Icons.alignR}</button>
        <button className="icon-btn" title="Align top" onClick={() => a('TOP')}>{Icons.alignT}</button>
        <button className="icon-btn" title="Align middle" onClick={() => a('CENTER_V')}>{Icons.alignCV}</button>
        <button className="icon-btn" title="Align bottom" onClick={() => a('BOTTOM')}>{Icons.alignB}</button>
        <button className="icon-btn" title="Distribute horizontally" onClick={() => app.editor.distribute(app.editor.selection, 'H')}>⇹</button>
        <button className="icon-btn" title="Tidy up (⌃⌥T)" onClick={() => app.editor.tidyUp(app.editor.selection)}>▦</button>
      </div>
    </div>
  );
}

function GeometrySection({ nodes }: { nodes: N[] }) {
  const one = nodes.length === 1 ? nodes[0] : null;
  const b = one ? app.doc.worldBounds(one.id) : boundsOfNodes(app.doc, nodes.map((n) => n.id))!;
  const parent = one ? app.doc.get(one.parent) : null;
  const rel = parent && parent.type !== 'PAGE';
  // X/Y shown relative to the parent frame (like design tools); W/H are the node's own size.
  const x = one ? one.x : b.x;
  const y = one ? one.y : b.y;
  const radius = same(nodes, (n) => (Array.isArray(n.cornerRadius) ? n.cornerRadius.join(',') : String(n.cornerRadius)));
  const [corners, setCorners] = useState(false);
  const radiusable = nodes.every((n) => ['RECTANGLE', 'FRAME', 'COMPONENT', 'INSTANCE', 'IMAGE', 'SECTION', 'STICKY', 'POLYGON', 'STAR'].includes(n.type));
  return (
    <div className="prop-section">
      <h4>
        <span>{rel ? `Position in ${parent!.name}` : 'Position'}</span>
      </h4>
      <div className="row">
        <NumberField label="X" value={x} onChange={(v) => (one ? update({ x: v }, 'Move') : app.editor.moveBy(app.editor.selection, v - b.x, 0))} />
        <NumberField label="Y" value={y} onChange={(v) => (one ? update({ y: v }, 'Move') : app.editor.moveBy(app.editor.selection, 0, v - b.y))} />
      </div>
      <div className="row">
        <NumberField label="W" min={0.01} value={same(nodes, (n) => n.width)} onChange={(v) => each((n) => (app.editor.resize(n.id, v, n.height), {}), 'Resize')} />
        <NumberField label="H" min={0.01} value={same(nodes, (n) => n.height)} onChange={(v) => each((n) => (app.editor.resize(n.id, n.width, v), {}), 'Resize')} />
      </div>
      <div className="row">
        <NumberField label="↻" title="Rotation" value={same(nodes, (n) => n.rotation)} suffix="°" onChange={(v) => update({ rotation: v }, 'Rotate')} />
        {radiusable ? (
          <NumberField label="◜" title="Corner radius" min={0} value={radius === 'mixed' || radius.includes(',') ? 'mixed' : Number(radius)} onChange={(v) => update({ cornerRadius: v }, 'Corner radius')} />
        ) : (
          <div style={{ flex: 1 }} />
        )}
        {radiusable && (
          <button className={`icon-btn ${corners ? 'on' : ''}`} title="Independent corners" onClick={() => setCorners(!corners)}>
            ⌗
          </button>
        )}
      </div>
      {corners && one && (
        <div className="row">
          {(['TL', 'TR', 'BR', 'BL'] as const).map((c, i) => {
            const arr = Array.isArray(one.cornerRadius) ? one.cornerRadius : [one.cornerRadius, one.cornerRadius, one.cornerRadius, one.cornerRadius];
            return <NumberField key={c} label={c} min={0} value={arr[i]} onChange={(v) => update({ cornerRadius: arr.map((r, j) => (j === i ? v : r)) as [number, number, number, number] })} />;
          })}
        </div>
      )}
      {corners && (
        <div className="row" title="Corner smoothing: 60% matches iOS">
          <span style={{ color: 'var(--text-2)', width: 64 }}>Smoothing</span>
          <input type="range" min={0} max={100} value={Math.round((one?.cornerSmoothing ?? 0) * 100)} onChange={(e) => update({ cornerSmoothing: +e.target.value / 100 }, 'Corner smoothing')} style={{ flex: 1 }} />
          <span style={{ width: 32, textAlign: 'right' }}>{Math.round((one?.cornerSmoothing ?? 0) * 100)}%</span>
        </div>
      )}
      {nodes.some((n) => isFrameLike(n.type)) && (
        <div className="row">
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--text-2)' }}>
            <input type="checkbox" checked={same(nodes, (n) => !!n.clipsContent) === true} onChange={(e) => update({ clipsContent: e.target.checked }, 'Clip content')} /> Clip content
          </label>
        </div>
      )}
    </div>
  );
}

function AutoLayoutSection({ nodes, frames, inAuto }: { nodes: N[]; frames: N[]; inAuto: boolean }) {
  const al = frames.filter((f) => isAutoLayout(f));
  const frameUpdate = (p: NodeProps) =>
    app.editor.transact('Auto layout', () => {
      for (const f of al) app.editor.update(f.id, p, 'Auto layout');
    });
  return (
    <div className="prop-section">
      <h4>
        <span>Auto layout</span>
        {frames.length > 0 &&
          (al.length ? (
            <button title="Remove auto layout" onClick={() => frames.forEach((f) => app.editor.update(f.id, { layoutMode: 'NONE' }, 'Remove auto layout'))}>
              {Icons.minus}
            </button>
          ) : (
            <button
              title="Add auto layout (⇧A)"
              onClick={() =>
                frames.forEach((f) => app.editor.update(f.id, { layoutMode: 'VERTICAL', itemSpacing: 10, paddingTop: 10, paddingRight: 10, paddingBottom: 10, paddingLeft: 10, layoutSizingH: 'HUG', layoutSizingV: 'HUG' }, 'Add auto layout'))
              }
            >
              {Icons.plus}
            </button>
          ))}
      </h4>
      {al.length > 0 && (
        <>
          <div className="row">
            <Seg
              value={same(al, (n) => (n.layoutWrap && n.layoutMode !== 'GRID' ? 'WRAP' : n.layoutMode!))}
              options={[
                { v: 'VERTICAL', label: Icons.autoV, title: 'Vertical' },
                { v: 'HORIZONTAL', label: Icons.autoH, title: 'Horizontal' },
                { v: 'WRAP', label: Icons.wrap, title: 'Wrap' },
                { v: 'GRID', label: '▦', title: 'Grid' },
              ]}
              onChange={(v) =>
                frameUpdate(
                  v === 'WRAP'
                    ? { layoutMode: 'HORIZONTAL', layoutWrap: true }
                    : v === 'GRID'
                      ? { layoutMode: 'GRID', layoutWrap: false, gridColumns: al[0].gridColumns ?? [{ type: 'FLEX', value: 1 }, { type: 'FLEX', value: 1 }] }
                      : { layoutMode: v as 'VERTICAL', layoutWrap: false },
                )
              }
            />
          </div>
          {al.every((f) => f.layoutMode === 'GRID') && (
            <div className="row">
              <NumberField
                label="▥"
                title="Columns"
                min={1}
                max={24}
                value={same(al, (n) => n.gridColumns?.length ?? 2)}
                onChange={(v) => frameUpdate({ gridColumns: Array.from({ length: Math.max(1, Math.round(v)) }, (_, i) => al[0].gridColumns?.[i] ?? { type: 'FLEX', value: 1 }) })}
              />
              <NumberField
                label="▤"
                title="Rows (more are added as needed)"
                min={0}
                max={48}
                value={same(al, (n) => n.gridRows?.length ?? 0)}
                onChange={(v) => frameUpdate({ gridRows: v > 0 ? Array.from({ length: Math.round(v) }, (_, i) => al[0].gridRows?.[i] ?? { type: 'HUG' }) : undefined })}
              />
              <NumberField label="↕" title="Row gap" value={same(al, (n) => n.counterAxisSpacing ?? n.itemSpacing ?? 0)} onChange={(v) => frameUpdate({ counterAxisSpacing: v })} />
            </div>
          )}
          <div className="row" style={{ alignItems: 'flex-start' }}>
            <AlignGrid frames={al} onChange={frameUpdate} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <NumberField label="⇔" title="Gap between items" value={same(al, (n) => n.itemSpacing ?? 0)} onChange={(v) => frameUpdate({ itemSpacing: v })} />
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--text-2)' }}>
                <input type="checkbox" checked={same(al, (n) => n.primaryAxisAlign === 'SPACE_BETWEEN') === true} onChange={(e) => frameUpdate({ primaryAxisAlign: e.target.checked ? 'SPACE_BETWEEN' : 'MIN' })} /> Space between
              </label>
            </div>
          </div>
          <div className="row">
            <NumberField label="↔" title="Horizontal padding" value={same(al, (n) => n.paddingLeft ?? 0)} onChange={(v) => frameUpdate({ paddingLeft: v, paddingRight: v })} />
            <NumberField label="↕" title="Vertical padding" value={same(al, (n) => n.paddingTop ?? 0)} onChange={(v) => frameUpdate({ paddingTop: v, paddingBottom: v })} />
          </div>
        </>
      )}
      <SizingRows nodes={nodes} inAuto={inAuto} />
      {nodes.length === 1 && app.doc.get(nodes[0].parent)?.layoutMode === 'GRID' && (
        <div className="row" title="Grid cell">
          <NumberField label="col" title="Column span" min={1} value={nodes[0].gridColumnSpan ?? 1} onChange={(v) => update({ gridColumnSpan: v > 1 ? Math.round(v) : undefined }, 'Grid span')} />
          <NumberField label="row" title="Row span" min={1} value={nodes[0].gridRowSpan ?? 1} onChange={(v) => update({ gridRowSpan: v > 1 ? Math.round(v) : undefined }, 'Grid span')} />
        </div>
      )}
    </div>
  );
}

function AlignGrid({ frames, onChange }: { frames: N[]; onChange: (p: NodeProps) => void }) {
  const f = frames[0];
  const horizontal = f.layoutMode === 'HORIZONTAL';
  const pa = f.primaryAxisAlign ?? 'MIN';
  const ca = f.counterAxisAlign ?? 'MIN';
  const vals = ['MIN', 'CENTER', 'MAX'] as const;
  return (
    <div className="align-grid">
      {[0, 1, 2].map((row) =>
        [0, 1, 2].map((col) => {
          const p = vals[horizontal ? col : row];
          const c = vals[horizontal ? row : col];
          const on = (pa === p || pa === 'SPACE_BETWEEN') && (ca === c || (ca === 'STRETCH' && c === 'MIN'));
          return <button key={`${row}${col}`} className={on ? 'on' : ''} onClick={() => onChange({ primaryAxisAlign: pa === 'SPACE_BETWEEN' ? 'SPACE_BETWEEN' : p, counterAxisAlign: c })} />;
        }),
      )}
    </div>
  );
}

function SizingRows({ nodes, inAuto }: { nodes: N[]; inAuto: boolean }) {
  const canHug = (n: N) => isAutoLayout(n) || n.type === 'TEXT';
  const opts = (axis: 'H' | 'V') => {
    const o: { v: 'FIXED' | 'HUG' | 'FILL'; label: string }[] = [{ v: 'FIXED', label: 'Fixed' }];
    if (nodes.every(canHug)) o.push({ v: 'HUG', label: 'Hug' });
    if (inAuto) o.push({ v: 'FILL', label: 'Fill' });
    void axis;
    return o;
  };
  const current = (n: N, axis: 'H' | 'V') => {
    const s = axis === 'H' ? n.layoutSizingH : n.layoutSizingV;
    if (s) return s;
    if (n.type === 'TEXT') return n.textAutoResize === 'WIDTH_AND_HEIGHT' || (axis === 'V' && n.textAutoResize === 'HEIGHT') ? 'HUG' : 'FIXED';
    return 'FIXED';
  };
  if (opts('H').length < 2) return null;
  const set = (axis: 'H' | 'V', v: 'FIXED' | 'HUG' | 'FILL') =>
    each((n) => {
      const p: NodeProps = axis === 'H' ? { layoutSizingH: v } : { layoutSizingV: v };
      if (n.type === 'TEXT') {
        if (axis === 'H') p.textAutoResize = v === 'HUG' ? 'WIDTH_AND_HEIGHT' : 'HEIGHT';
        else p.textAutoResize = v === 'HUG' ? (n.textAutoResize === 'WIDTH_AND_HEIGHT' ? 'WIDTH_AND_HEIGHT' : 'HEIGHT') : 'NONE';
      }
      return p;
    }, 'Resizing');
  return (
    <div className="row" style={{ marginTop: 6 }}>
      <Select label="W" value={same(nodes, (n) => current(n, 'H'))} options={opts('H')} onChange={(v) => set('H', v)} />
      <Select label="H" value={same(nodes, (n) => current(n, 'V'))} options={opts('V')} onChange={(v) => set('V', v)} />
    </div>
  );
}

function ConstraintsSection({ node }: { node: N }) {
  const opts = [
    { v: 'MIN', label: 'Left' },
    { v: 'MAX', label: 'Right' },
    { v: 'CENTER', label: 'Center' },
    { v: 'STRETCH', label: 'Left & right' },
    { v: 'SCALE', label: 'Scale' },
  ] as const;
  const vopts = [
    { v: 'MIN', label: 'Top' },
    { v: 'MAX', label: 'Bottom' },
    { v: 'CENTER', label: 'Center' },
    { v: 'STRETCH', label: 'Top & bottom' },
    { v: 'SCALE', label: 'Scale' },
  ] as const;
  return (
    <div className="prop-section">
      <h4>Constraints</h4>
      <div className="row">
        <Select value={node.constraintH ?? 'MIN'} options={[...opts]} onChange={(v) => update({ constraintH: v }, 'Constraints')} />
        <Select value={node.constraintV ?? 'MIN'} options={[...vopts]} onChange={(v) => update({ constraintV: v }, 'Constraints')} />
      </div>
    </div>
  );
}

function LayerSection({ nodes }: { nodes: N[] }) {
  return (
    <div className="prop-section">
      <h4>Layer</h4>
      <div className="row">
        <NumberField label="◐" title="Opacity" min={0} max={100} suffix="%" value={(() => {
          const v = same(nodes, (n) => n.opacity);
          return v === 'mixed' ? v : Math.round(v * 100);
        })()} onChange={(v, live) => update({ opacity: v / 100 }, live ? 'Opacity' : 'Opacity')} />
        <button className="icon-btn" title="Show/Hide" onClick={() => update({ visible: !nodes.every((n) => n.visible) }, 'Toggle visibility')}>
          {nodes.every((n) => n.visible) ? Icons.eye : Icons.eyeOff}
        </button>
        <button className={`icon-btn ${nodes.every((n) => n.locked) ? 'on' : ''}`} title="Lock" onClick={() => update({ locked: !nodes.every((n) => n.locked) }, 'Toggle lock')}>
          {Icons.lock}
        </button>
      </div>
      <div className="row">
        <Select
          value={same(nodes, (n) => n.blendMode ?? (isContainerType(n.type) ? 'PASS_THROUGH' : 'NORMAL'))}
          options={[
            ...(nodes.every((n) => isContainerType(n.type)) ? [{ v: 'PASS_THROUGH', label: 'Pass through' }] : []),
            ...BLEND_OPTIONS.map((m) => ({ v: m, label: m.charAt(0) + m.slice(1).toLowerCase().replace(/_/g, ' ') })),
          ]}
          onChange={(v) => update({ blendMode: v as NodeData['blendMode'] }, 'Blend mode')}
        />
      </div>
    </div>
  );
}

const FONT_FAMILIES = ['Inter', 'system-ui', 'Georgia', 'Times New Roman', 'Helvetica', 'Arial', 'Courier New', 'Menlo', 'Verdana', 'Trebuchet MS', 'Comic Sans MS'];
const WEIGHTS = [
  { v: '100', label: 'Thin' },
  { v: '200', label: 'Extra Light' },
  { v: '300', label: 'Light' },
  { v: '400', label: 'Regular' },
  { v: '500', label: 'Medium' },
  { v: '600', label: 'Semi Bold' },
  { v: '700', label: 'Bold' },
  { v: '800', label: 'Extra Bold' },
  { v: '900', label: 'Black' },
];

function TextSection({ nodes }: { nodes: N[] }) {
  // OpenType features the selected fonts have (unknown until a font has loaded: then all are offered)
  const featureSets = nodes.map((n) => getTextShaper()?.featuresFor?.(n.fontFamily || 'Inter', n.fontWeight ?? 400, !!n.italic) ?? null);
  const hasFeature = (tag: string) => featureSets.some((x) => !x) || featureSets.every((x) => x!.has(tag.toLowerCase()));
  const fam = same(nodes, (n) => n.fontFamily ?? 'Inter');
  const uploaded = useApp('ui', () => libraryFamilies().join('|'))
    .split('|')
    .filter(Boolean);
  const fileInput = useRef<HTMLInputElement>(null);
  const families = [...uploaded, ...FONT_FAMILIES.filter((f) => !uploaded.includes(f))];
  // fonts on this computer (asked for once; the browser or desktop app may ask permission)
  const localState = useApp('ui', () => localFontStatus());
  const local = localState === 'ready' ? localFamilies(new Set(families.map((f) => f.toLowerCase()))) : [];
  const pick = (v: string) => (v === '__upload' ? fileInput.current?.click() : v === '__local' ? void discoverLocalFonts(true).then((ok) => !ok && app.toast('Fonts on this computer are unavailable (permission denied or not supported)')) : update({ fontFamily: v }, 'Font'));
  const localOnly = fam !== 'mixed' && local.includes(fam) && !uploaded.includes(fam);
  return (
    <div className="prop-section">
      <h4>Text</h4>
      <StyleChip nodes={nodes} kind="textStyleId" />
      <div className="row">
        <Select
          value={fam}
          options={[
            ...(fam !== 'mixed' && !families.includes(fam) && !local.includes(fam) ? [{ v: fam, label: fam }] : []),
            ...uploaded.map((f) => ({ v: f, label: f, group: 'Shared on this server' })),
            ...FONT_FAMILIES.filter((f) => !uploaded.includes(f)).map((f) => ({ v: f, label: f, group: uploaded.length || local.length ? 'Fonts' : undefined })),
            ...local.map((f) => ({ v: f, label: f, group: 'On this computer' })),
            ...(localState === 'idle' ? [{ v: '__local', label: 'Show fonts on this computer…' }] : []),
            { v: '__upload', label: 'Upload a font…' },
          ]}
          onChange={pick}
        />
        <input
          ref={fileInput}
          type="file"
          accept=".ttf,.otf,.woff,.woff2"
          multiple
          hidden
          onChange={async (e) => {
            const files = e.target.files;
            if (!files?.length) return;
            const added = await uploadFonts(files);
            if (added[0]) update({ fontFamily: added[0] }, 'Font');
            e.target.value = '';
          }}
        />
      </div>
      {localOnly && (
        <div className="row" style={{ color: 'var(--text-2)', fontSize: 11, gap: 6, flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 140 }}>Only on this computer: others see a fallback font.</span>
          <button className="btn secondary" title="Upload this family to the server's font library so everyone (and agents) can use it. Check the font's license allows it." onClick={() => void shareLocalFamily(fam)}>
            Share font
          </button>
        </div>
      )}
      <div className="row">
        <Select value={(() => {
          const v = same(nodes, (n) => String(n.fontWeight ?? 400));
          return v;
        })()} options={WEIGHTS} onChange={(v) => update({ fontWeight: Number(v) }, 'Font weight')} />
        <NumberField label="Aa" title="Font size" min={1} value={same(nodes, (n) => n.fontSize ?? 16)} onChange={(v) => each((n) => (n.type === 'STICKY' ? { fontSize: v, pluginData: { ...n.pluginData, autoFont: 'false' } } : { fontSize: v }), 'Font size')} />
      </div>
      <div className="row">
        <NumberField label="↕" title="Line height (0 = auto)" min={0} value={same(nodes, (n) => n.lineHeight ?? 0)} onChange={(v) => update({ lineHeight: v }, 'Line height')} />
        <NumberField label="⇿" title="Letter spacing" value={same(nodes, (n) => n.letterSpacing ?? 0)} onChange={(v) => update({ letterSpacing: v }, 'Letter spacing')} />
      </div>
      <div className="row">
        <Seg
          value={same(nodes, (n) => n.textAlign ?? 'LEFT')}
          options={[
            { v: 'LEFT', label: '⇤', title: 'Left' },
            { v: 'CENTER', label: '↔', title: 'Center' },
            { v: 'RIGHT', label: '⇥', title: 'Right' },
            { v: 'JUSTIFIED', label: '≡', title: 'Justified' },
          ]}
          onChange={(v) => update({ textAlign: v }, 'Text align')}
        />
        <Seg
          value={same(nodes, (n) => n.verticalAlign ?? 'TOP')}
          options={[
            { v: 'TOP', label: '⤒', title: 'Top' },
            { v: 'CENTER', label: '⇕', title: 'Middle' },
            { v: 'BOTTOM', label: '⤓', title: 'Bottom' },
          ]}
          onChange={(v) => update({ verticalAlign: v }, 'Vertical align')}
        />
      </div>
      <div className="row">
        <Seg
          value={same(nodes, (n) => n.textDecoration ?? 'NONE')}
          options={[
            { v: 'NONE', label: '—', title: 'None' },
            { v: 'UNDERLINE', label: <u>U</u>, title: 'Underline' },
            { v: 'STRIKETHROUGH', label: <s>S</s>, title: 'Strikethrough' },
          ]}
          onChange={(v) => update({ textDecoration: v }, 'Decoration')}
        />
        <Seg
          value={same(nodes, (n) => n.textCase ?? 'ORIGINAL')}
          options={[
            { v: 'ORIGINAL', label: '—', title: 'As typed' },
            { v: 'UPPER', label: 'AG', title: 'Uppercase' },
            { v: 'LOWER', label: 'ag', title: 'Lowercase' },
            { v: 'TITLE', label: 'Ag', title: 'Title case' },
          ]}
          onChange={(v) => update({ textCase: v }, 'Case')}
        />
      </div>
      <div className="row">
        <Seg
          value={same(nodes, (n) => {
            const f = paragraphFormats(n);
            if (!f) return 'NONE';
            const kinds = new Set(f.map((x) => x.list ?? 'NONE'));
            return kinds.size === 1 ? [...kinds][0] : 'mixed';
          })}
          options={[
            { v: 'NONE', label: '¶', title: 'No list' },
            { v: 'BULLET', label: '•≡', title: 'Bulleted list' },
            { v: 'NUMBER', label: '1≡', title: 'Numbered list' },
          ]}
          onChange={(v) =>
            app.editor.transact('List', () => {
              // the whole layer; while editing, the format bar applies lists to the selected paragraphs
              for (const n of nodes) app.editor.formatParagraphs(n.id, 0, (n.characters ?? '').length, (c) => ({ list: v, level: c.level }));
            })
          }
        />
      </div>
      <div className="row" title="OpenType features">
        {([
          ['TNUM', '12', 'Tabular figures'],
          ['SMCP', 'Sᴍᴄ', 'Small caps'],
          ['C2SC', 'ᴀʟʟ', 'All small caps'],
          ['LIGA', 'ﬁ', 'Ligatures'],
        ] as const).map(([k, label, title]) => {
          const on = same(nodes, (n) => (k === 'LIGA' ? n.openTypeFeatures?.LIGA !== false : !!n.openTypeFeatures?.[k]));
          const inFont = hasFeature(k);
          // small caps are synthesized when the font has none; the others need the font
          const synth = !inFont && (k === 'SMCP' || k === 'C2SC');
          const missing = !inFont && !synth;
          return (
            <button
              key={k}
              className={`btn secondary${on === true ? ' active' : ''}`}
              style={{ padding: '0 8px', opacity: on === true && !missing ? 1 : missing ? 0.3 : 0.55 }}
              disabled={missing && on !== true && k !== 'LIGA'}
              title={missing ? `${title}: not in this font` : synth ? `${title} (synthesized: this font has none)` : title}
              onClick={() =>
                app.editor.transact(title, () => {
                  for (const n of nodes) {
                    const f = { ...(n.openTypeFeatures ?? {}) };
                    const cur = k === 'LIGA' ? f.LIGA !== false : !!f[k];
                    if (k === 'LIGA') {
                      if (cur) f.LIGA = false;
                      else delete f.LIGA;
                    } else if (cur) delete f[k];
                    else f[k] = true;
                    app.editor.update(n.id, { openTypeFeatures: Object.keys(f).length ? f : undefined }, title);
                  }
                })
              }
            >
              {label}
            </button>
          );
        })}
        <select
          className="share-select"
          style={{ flex: 1, minWidth: 0 }}
          value=""
          title="More OpenType features (the font decides which it has)"
          onChange={(e) => {
            const k = e.target.value;
            if (!k) return;
            app.editor.transact('OpenType feature', () => {
              for (const n of nodes) {
                const f = { ...(n.openTypeFeatures ?? {}) };
                if (f[k]) delete f[k];
                else f[k] = true;
                app.editor.update(n.id, { openTypeFeatures: Object.keys(f).length ? f : undefined }, 'OpenType feature');
              }
            });
          }}
        >
          <option value="">More…</option>
          {MORE_FEATURES.filter(([k]) => hasFeature(k) || nodes.some((n) => n.openTypeFeatures?.[k])).length === 0 && <option disabled>No other features in this font</option>}
          {MORE_FEATURES.filter(([k]) => hasFeature(k) || nodes.some((n) => n.openTypeFeatures?.[k])).map(([k, label]) => (
            <option key={k} value={k}>
              {nodes.every((n) => n.openTypeFeatures?.[k]) ? '✓ ' : ''}
              {label}
            </option>
          ))}
        </select>
      </div>
      {(() => {
        const on = MORE_FEATURES.filter(([k]) => nodes.every((n) => n.openTypeFeatures?.[k]));
        return on.length ? <div className="row" style={{ color: 'var(--text-2)', flexWrap: 'wrap', gap: 4 }}>{on.map(([k, l]) => <span key={k} className="chip">{l}</span>)}</div> : null;
      })()}
      {nodes.length === 1 && nodes[0].textPath && (
        <div className="row">
          <NumberField label="Offset" title="Where the text starts along the path" value={nodes[0].textPathOffset ?? 0} onChange={(v) => update({ textPathOffset: v || undefined }, 'Path offset')} />
          <button className="btn secondary" title="Put the text on the other side of the path" onClick={() => update({ textPath: reversePath(nodes[0].textPath!) }, 'Flip text side')}>
            ⇅ Flip
          </button>
          <button className="btn secondary" title="Detach the text from its path" onClick={() => update({ textPath: undefined, textPathOffset: undefined, textAutoResize: 'WIDTH_AND_HEIGHT' }, 'Detach from path')}>
            Detach
          </button>
        </div>
      )}
      {nodes.every((n) => n.type === 'TEXT' && !n.textPath) && (
        <div className="row">
          <Seg
            value={same(nodes, (n) => n.textAutoResize ?? 'NONE')}
            options={[
              { v: 'WIDTH_AND_HEIGHT', label: 'Auto width' },
              { v: 'HEIGHT', label: 'Auto height' },
              { v: 'NONE', label: 'Fixed' },
            ]}
            onChange={(v) => update({ textAutoResize: v }, 'Text resizing')}
          />
        </div>
      )}
      {nodes.every((n) => n.type === 'TEXT' && !n.textPath) && (
        <div className="row">
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--text-2)', flex: 1 }} title="Text that doesn't fit ends with an ellipsis">
            <input type="checkbox" checked={nodes.every((n) => n.textTruncation === 'ENDING')} onChange={(e) => update({ textTruncation: e.target.checked ? 'ENDING' : 'DISABLED', ...(e.target.checked ? {} : { maxLines: undefined }) }, 'Truncate text')} /> Truncate text
          </label>
          {nodes.every((n) => n.textTruncation === 'ENDING') && <NumberField label="lines" value={same(nodes, (n) => n.maxLines ?? 0) ?? 0} min={0} onChange={(v) => update({ maxLines: v > 0 ? Math.round(v) : undefined }, 'Max lines')} />}
        </div>
      )}
      {nodes.some((n) => n.type !== 'TEXT') && (
        <div className="paint-row">
          <div className="field">
            {(() => {
              const c = nodes.find((n) => n.textColor)?.textColor ?? { r: 0.12, g: 0.12, b: 0.12, a: 1 };
              return (
                <>
                  <ColorSwatch color={c} onChange={(col) => update({ textColor: col }, 'Text color')} />
                  <HexField color={c} onChange={(col) => update({ textColor: col }, 'Text color')} />
                  <span style={{ color: 'var(--text-2)' }}>Text color</span>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

function StyleChip({ nodes, kind }: { nodes: N[]; kind: 'fillStyleId' | 'strokeStyleId' | 'textStyleId' | 'effectStyleId' | 'gridStyleId' }) {
  const id = same(nodes, (n) => n[kind] ?? null);
  if (!id || id === 'mixed') return null;
  const st = app.doc.get(id);
  if (!st) return null;
  return (
    <div className="row">
      <span className="chip" style={{ flex: 1 }}>
        ◈ {st.name}
      </span>
      <button className="icon-btn" title="Detach style" onClick={() => update({ [kind]: undefined } as NodeProps, 'Detach style')}>
        ⛓
      </button>
    </div>
  );
}

const PROP_LABELS: Record<string, string> = {
  fill: 'Fill',
  stroke: 'Stroke',
  textColor: 'Text color',
  opacity: 'Opacity',
  width: 'Width',
  height: 'Height',
  cornerRadius: 'Corner radius',
  strokeWeight: 'Stroke weight',
  itemSpacing: 'Gap',
  paddingTop: 'Padding top',
  paddingRight: 'Padding right',
  paddingBottom: 'Padding bottom',
  paddingLeft: 'Padding left',
  fontSize: 'Font size',
  lineHeight: 'Line height',
  letterSpacing: 'Letter spacing',
  characters: 'Text',
  visible: 'Visibility',
};

/** The properties of this layer that can take a variable. */
function bindableProps(n: N): string[] {
  const out = ['fill', 'opacity', 'width', 'height', 'visible'];
  if (n.type === 'TEXT') out.push('fontSize', 'lineHeight', 'letterSpacing', 'characters');
  else out.push('stroke', 'strokeWeight', 'cornerRadius');
  if (n.type === 'STICKY' || n.type === 'SHAPE_WITH_TEXT') out.push('textColor');
  if (autoLayoutOn(n)) out.push('itemSpacing', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft');
  return out;
}

/** Bind a property to a variable: only variables whose type and scopes fit are offered. */
function BindPicker({ node }: { node: N }) {
  const [prop, setProp] = useState<string | null>(null);
  const props = bindableProps(node).filter((p) => !node.boundVariables?.[p]);
  if (!props.length) return null;
  const choices = prop ? bindableVariables(app.doc, node, prop) : [];
  return (
    <div className="row">
      <Select value={prop ?? ''} options={[{ v: '', label: '+ Bind variable…' }, ...props.map((p) => ({ v: p, label: PROP_LABELS[p] ?? p }))]} onChange={(v) => setProp(v || null)} />
      {prop && (
        <Select
          value=""
          options={[{ v: '', label: choices.length ? 'Choose…' : 'No variable fits' }, ...choices.map((v) => ({ v: v.id, label: v.name }))]}
          onChange={(id) => {
            if (!id) return;
            try {
              app.editor.bindVariable([node.id], prop, id);
            } catch (e) {
              app.toast((e as Error).message);
            }
            setProp(null);
          }}
        />
      )}
    </div>
  );
}

function BoundChips({ nodes }: { nodes: N[] }) {
  const one = nodes.length === 1 ? nodes[0] : null;
  const hasVars = useApp('doc', () => app.doc.children(app.doc.rootId).some((c) => c.type === 'VARIABLE_COLLECTION'));
  const b = one?.boundVariables ?? {};
  if (!one || (!Object.keys(b).length && !hasVars)) return null;
  return (
    <div className="prop-section">
      <h4>Variables</h4>
      {Object.entries(b).map(([prop, vid]) => (
        <div className="row" key={prop}>
          <span style={{ color: 'var(--text-2)', width: 80 }}>{prop}</span>
          <span className="chip" style={{ flex: 1 }}>
            ◆ {app.doc.get(vid)?.name ?? vid}
          </span>
          <button className="icon-btn" title="Unbind" onClick={() => app.editor.bindVariable([one.id], prop, null)}>
            {Icons.minus}
          </button>
        </div>
      ))}
      {hasVars && <BindPicker key={one.id} node={one} />}
    </div>
  );
}

function ModesSection({ nodes }: { nodes: N[] }) {
  // collections with modes to pick, and extended collections (choosing one applies its overrides)
  const cols = [...app.doc.children(app.doc.rootId)].filter((n) => n.type === 'VARIABLE_COLLECTION' && ((n.modes?.length ?? 0) > 1 || n.extendsCollection));
  const one = nodes.length === 1 ? nodes[0] : null;
  if (!cols.length || !one || !['FRAME', 'COMPONENT', 'INSTANCE', 'SECTION', 'GROUP'].includes(one.type)) return null;
  return (
    <div className="prop-section">
      <h4>Variable modes</h4>
      {cols.map((c) => (
        <div className="row" key={c.id}>
          <Select
            label={c.name}
            value={one.variableModes?.[c.id] ?? 'auto'}
            options={[{ v: 'auto', label: 'Auto (inherit)' }, ...(c.modes ?? []).map((m) => ({ v: m.id, label: m.name }))]}
            onChange={(v) => app.editor.setVariableMode(one.id, c.id, v === 'auto' ? null : v)}
          />
        </div>
      ))}
    </div>
  );
}

function PaintsSection({ nodes, kind, title }: { nodes: N[]; kind: 'fills' | 'strokes'; title: string }) {
  const paints = same(nodes, (n) => n[kind]);
  const set = (ps: Paint[]) => update({ [kind]: ps } as NodeProps, kind === 'fills' ? 'Fill' : 'Stroke');
  const list = paints === 'mixed' ? null : paints;
  return (
    <div className="prop-section">
      <h4>
        <span>{title}</span>
        <button
          title={`Add ${kind === 'fills' ? 'fill' : 'stroke'}`}
          onClick={() => {
            if (list === null) set([{ type: 'SOLID', color: parseColor('#d9d9d9') }]);
            else set([...list, { type: 'SOLID', color: parseColor(kind === 'fills' ? (list.length ? '#000000' : '#d9d9d9') : '#000000') }]);
            if (kind === 'strokes' && nodes.every((n) => !n.strokeWeight)) update({ strokeWeight: 1 });
          }}
        >
          {Icons.plus}
        </button>
      </h4>
      <StyleChip nodes={nodes} kind={kind === 'fills' ? 'fillStyleId' : 'strokeStyleId'} />
      {list === null && <div style={{ color: 'var(--text-2)' }}>Mixed — click + to replace</div>}
      {list?.map((p, i) => (
        <PaintRow
          key={i}
          paint={p}
          onChange={(np) => set(list.map((x, j) => (j === i ? np : x)))}
          onRemove={() => set(list.filter((_, j) => j !== i))}
          slot={kind === 'fills' && nodes.length === 1 ? { nodeId: nodes[0].id, index: i } : undefined}
        />
      ))}
      {kind === 'strokes' && list && list.length > 0 && (
        <>
          <div className="row">
            <NumberField label="≡" title="Stroke weight" min={0} value={nodes.some((n) => n.strokeWeights) ? 'mixed' : same(nodes, (n) => n.strokeWeight)} onChange={(v) => update({ strokeWeight: v, strokeWeights: null } as never, 'Stroke weight')} />
            <Select
              value={same(nodes, (n) => n.strokeAlign)}
              options={[
                { v: 'INSIDE', label: 'Inside' },
                { v: 'CENTER', label: 'Center' },
                { v: 'OUTSIDE', label: 'Outside' },
              ]}
              onChange={(v) => update({ strokeAlign: v }, 'Stroke align')}
            />
          </div>
          {nodes.length === 1 && sideWeights({ ...nodes[0], strokeWeights: [0, 0, 0, 0] }) && <SideStrokes key={nodes[0].id} node={nodes[0]} />}
          <div className="row">
            <Select
              value={same(nodes, (n) => (n.strokeDash?.length ? 'DASH' : 'SOLID'))}
              options={[
                { v: 'SOLID', label: 'Solid' },
                { v: 'DASH', label: 'Dashed' },
              ]}
              onChange={(v) => update({ strokeDash: v === 'DASH' ? [8, 6] : undefined }, 'Dash')}
            />
            <Select
              value={same(nodes, (n) => n.strokeCap ?? 'NONE')}
              options={[
                { v: 'NONE', label: 'No cap' },
                { v: 'ROUND', label: 'Round cap' },
                { v: 'SQUARE', label: 'Square cap' },
              ]}
              onChange={(v) => update({ strokeCap: v }, 'Cap')}
            />
          </div>
          {nodes.every((n) => n.type === 'LINE') && (
            <div className="row">
              <CapSelect nodes={nodes} which="startCap" />
              <CapSelect nodes={nodes} which="endCap" />
            </div>
          )}
          <div className="row" title="Brushes, variable width and dynamic strokes (Figma Draw)">
            <Select
              label="Brush"
              value={same(nodes, (n) => (n.strokeBrush ? (n.strokeBrush.sourceId ? `brush:${n.strokeBrush.sourceId}` : (n.strokeBrush.preset ?? n.strokeBrush.type.toLowerCase())) : 'NONE'))}
              options={[
                { v: 'NONE', label: 'None' },
                ...STRETCH_PRESETS.map((p) => ({ v: p, label: p[0].toUpperCase() + p.slice(1) })),
                ...SCATTER_PRESETS.map((p) => ({ v: p, label: `Scatter: ${p}` })),
                ...customBrushes().map((b) => ({ v: `brush:${b.id}`, label: `${b.brushDefinition === 'SCATTER' ? 'Scatter' : 'Stretch'}: ${b.name}` })),
                ...[...new Set(nodes.map((n) => n.strokeBrush?.sourceId).filter((id): id is NodeId => !!id && !app.doc.get(id)?.brushDefinition))].map((id) => ({ v: `brush:${id}`, label: `Custom: ${app.doc.get(id)?.name ?? 'missing layer'}` })),
              ]}
              onChange={(v) => {
                if (v.startsWith('brush:')) {
                  const src = app.doc.get(v.slice(6));
                  const type = src?.brushDefinition ?? nodes[0].strokeBrush?.type ?? 'STRETCH';
                  if (src) update({ strokeBrush: { type, name: src.name, sourceId: src.id, ...(type === 'SCATTER' ? { gap: 1.5, sizeJitter: 0.2, angleJitter: 0.1 } : {}) } } as NodeProps, 'Brush');
                  return;
                }
                update({ strokeBrush: v === 'NONE' ? undefined : { type: SCATTER_PRESETS.includes(v) ? 'SCATTER' : 'STRETCH', preset: v, ...(SCATTER_PRESETS.includes(v) ? { sizeJitter: 0.3, angleJitter: 0.2 } : {}) } } as NodeProps, 'Brush');
              }}
            />
            <Select
              label="Width"
              value={same(nodes, (n) => {
                const p = JSON.stringify(n.strokeWidthProfile ?? null);
                return Object.entries(WIDTH_PROFILES).find(([, v]) => JSON.stringify(v) === p)?.[0] ?? (n.strokeWidthProfile ? 'custom' : 'uniform');
              })}
              options={[...Object.keys(WIDTH_PROFILES).map((k) => ({ v: k, label: k[0].toUpperCase() + k.slice(1).replace('-', ' ') })), ...(nodes.some((n) => n.strokeWidthProfile && !Object.values(WIDTH_PROFILES).some((v) => JSON.stringify(v) === JSON.stringify(n.strokeWidthProfile))) ? [{ v: 'custom', label: 'Custom' }] : [])]}
              onChange={(v) => v !== 'custom' && update({ strokeWidthProfile: v === 'uniform' ? undefined : WIDTH_PROFILES[v] } as NodeProps, 'Stroke width profile')}
            />
          </div>
          <div className="row" title="Dynamic stroke: the line wobbles">
            <NumberField label="∿" title="Wiggle (px, 0 = off)" min={0} value={same(nodes, (n) => n.strokeDynamic?.wiggle ?? 0)} onChange={(v) => update({ strokeDynamic: v > 0 ? { frequency: nodes[0].strokeDynamic?.frequency ?? 6, wiggle: v } : undefined } as NodeProps, 'Dynamic stroke')} />
            <NumberField label="f" title="Frequency (waves per 100px)" min={0} value={same(nodes, (n) => n.strokeDynamic?.frequency ?? 6)} onChange={(v) => nodes[0].strokeDynamic && update({ strokeDynamic: { ...nodes[0].strokeDynamic, frequency: v } } as NodeProps, 'Dynamic stroke')} />
          </div>
        </>
      )}
    </div>
  );
}

function CapSelect({ nodes, which }: { nodes: N[]; which: 'startCap' | 'endCap' }) {
  return (
    <Select
      label={which === 'startCap' ? 'Start' : 'End'}
      value={same(nodes, (n) => n[which] ?? 'NONE')}
      options={[
        { v: 'NONE', label: 'None' },
        { v: 'ARROW', label: 'Arrow' },
        { v: 'TRIANGLE', label: 'Triangle' },
        { v: 'CIRCLE', label: 'Circle' },
        { v: 'DIAMOND', label: 'Diamond' },
      ]}
      onChange={(v) => update({ [which]: v } as NodeProps, 'Line ends')}
    />
  );
}

const ADJUSTMENTS = ['exposure', 'contrast', 'saturation', 'temperature'] as const;

const MORE_FEATURES: [string, string][] = [
  ['FRAC', 'Fractions'],
  ['ZERO', 'Slashed zero'],
  ['CASE', 'Case-sensitive forms'],
  ['ONUM', 'Old-style figures'],
  ['SUPS', 'Superscript'],
  ['SUBS', 'Subscript'],
  ...Array.from({ length: 8 }, (_, i): [string, string] => [`SS0${i + 1}`, `Stylistic set ${i + 1}`]),
  ['CV11', 'Single-storey a'],
];

function PaintRow({ paint, onChange, onRemove, slot }: { paint: Paint; onChange: (p: Paint) => void; onRemove: () => void; slot?: { nodeId: NodeId; index: number } }) {
  const hidden = paint.visible === false;
  const [adjusting, setAdjusting] = useState(false);
  return (
    <>
    <div className="paint-row">
      <div className="field" style={{ opacity: hidden ? 0.5 : 1 }}>
        {paint.type === 'SOLID' && (
          <>
            <ColorSwatch color={paint.color} onChange={(c) => onChange({ ...paint, color: c })} />
            <HexField color={paint.color} onChange={(c) => onChange({ ...paint, color: c })} />
            <input
              style={{ border: 0, background: 'transparent', outline: 'none', width: 36, textAlign: 'right' }}
              defaultValue={`${Math.round(paint.color.a * (paint.opacity ?? 1) * 100)}%`}
              key={`${paint.color.a}:${paint.opacity}`}
              onBlur={(e) => {
                const v = parseFloat(e.target.value);
                if (Number.isFinite(v)) onChange({ ...paint, color: { ...paint.color, a: Math.max(0, Math.min(1, v / 100)) }, opacity: undefined });
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
            />
          </>
        )}
        {'stops' in paint && (
          <>
            {paint.stops.slice(0, 4).map((s, i) => (
              <ColorSwatch key={i} color={s.color} onChange={(c) => onChange({ ...paint, stops: paint.stops.map((x, j) => (j === i ? { ...x, color: c } : x)) })} />
            ))}
            <span style={{ color: 'var(--text-2)' }}>{({ GRADIENT_LINEAR: 'Linear', GRADIENT_RADIAL: 'Radial', GRADIENT_ANGULAR: 'Angular', GRADIENT_DIAMOND: 'Diamond' } as Record<string, string>)[paint.type]}</span>
          </>
        )}
        {paint.type === 'IMAGE' && <span style={{ color: 'var(--text-2)', flex: 1 }}>Image · {paint.scaleMode ?? 'FILL'}</span>}
        {paint.type === 'SHADER' && <span style={{ color: 'var(--text-2)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Shader · {paint.name ?? 'Custom'}</span>}
        {paint.type === 'PATTERN' && (
          <select
            title="Pattern tile: the layer that repeats"
            style={{ border: 0, background: 'transparent', flex: 1, minWidth: 0, color: 'inherit' }}
            value={paint.sourceId}
            onChange={(e) => onChange({ ...paint, sourceId: e.target.value })}
          >
            {patternSources(paint.sourceId).map((n) => (
              <option key={n.id} value={n.id}>
                Pattern · {n.name}
              </option>
            ))}
          </select>
        )}
        {paint.type === 'IMAGE' && (
          <button className="icon-btn" title="Adjust image" style={{ width: 20, opacity: paint.filters ? 1 : 0.6 }} onClick={() => setAdjusting((a) => !a)}>
            ☼
          </button>
        )}
      </div>
      <select
        className="icon-btn"
        style={{ width: 28, border: 0, appearance: 'none', textAlign: 'center' }}
        title="Paint type"
        value={paint.type}
        onChange={(e) => {
          const t = e.target.value;
          const base = paint.type === 'SOLID' ? paint.color : paint.type === 'IMAGE' || paint.type === 'PATTERN' || paint.type === 'SHADER' ? parseColor('#d9d9d9') : paint.stops[0].color;
          const centered = t === 'GRADIENT_ANGULAR' || t === 'GRADIENT_DIAMOND';
          if (t === 'SOLID') onChange({ type: 'SOLID', color: base });
          else if (t === 'SHADER') {
            const p = shaderPreset('grain-gradient')!;
            onChange({ type: 'SHADER', name: p.name, source: p.source });
          }
          else if (t === 'PATTERN') {
            // the tile: another layer on the page (Figma Draw pattern fills repeat a layer)
            const src = patternSources().find((n) => !app.editor.selection.includes(n.id));
            if (!src) app.toast('Make the layer you want to repeat first, then pick it as the pattern');
            else onChange({ type: 'PATTERN', sourceId: src.id, scale: 1, spacing: { x: 8, y: 8 } });
          }
          else if (t === 'GRADIENT_LINEAR' || t === 'GRADIENT_RADIAL' || centered)
            onChange({ type: t, stops: [{ position: 0, color: base }, { position: 1, color: { ...base, a: 0 } }], from: centered ? { x: 0.5, y: 0.5 } : { x: 0.5, y: 0 }, to: centered ? { x: 1, y: 0.5 } : { x: 0.5, y: 1 } });
        }}
      >
        <option value="SOLID">■</option>
        <option value="GRADIENT_LINEAR">▤</option>
        <option value="GRADIENT_RADIAL">◎</option>
        <option value="GRADIENT_ANGULAR">◔</option>
        <option value="GRADIENT_DIAMOND">◇</option>
        {paint.type === 'IMAGE' && <option value="IMAGE">▣</option>}
        <option value="PATTERN">▦</option>
        <option value="SHADER">✦</option>
      </select>
      <button className="icon-btn" title={hidden ? 'Show' : 'Hide'} onClick={() => onChange({ ...paint, visible: hidden ? undefined : false })}>
        {hidden ? Icons.eyeOff : Icons.eye}
      </button>
      <button className="icon-btn" title="Remove" onClick={onRemove}>
        {Icons.minus}
      </button>
    </div>
    {paint.type === 'SHADER' && <ShaderControls kind="fill" shader={paint} slot={slot} onChange={(x) => onChange({ ...paint, ...x })} />}
    {paint.type === 'PATTERN' && (
      <div className="row">
        <NumberField label="%" title="Tile scale" min={1} value={Math.round((paint.scale ?? 1) * 100)} onChange={(v) => onChange({ ...paint, scale: Math.max(0.01, v / 100) })} />
        <NumberField label="↔" title="Spacing" value={paint.spacing?.x ?? 0} onChange={(v) => onChange({ ...paint, spacing: { x: v, y: paint.spacing?.y ?? 0 } })} />
        <NumberField label="↕" title="Spacing" value={paint.spacing?.y ?? 0} onChange={(v) => onChange({ ...paint, spacing: { x: paint.spacing?.x ?? 0, y: v } })} />
        <Select
          value={paint.tile ?? 'RECTANGULAR'}
          options={[
            { v: 'RECTANGULAR', label: 'Grid' },
            { v: 'HORIZONTAL_HEXAGONAL', label: 'Hex rows' },
            { v: 'VERTICAL_HEXAGONAL', label: 'Hex columns' },
          ]}
          onChange={(v) => onChange({ ...paint, tile: v === 'RECTANGULAR' ? undefined : (v as 'HORIZONTAL_HEXAGONAL') })}
        />
      </div>
    )}
    {paint.type === 'IMAGE' && adjusting && (
      <div style={{ padding: '2px 0 6px' }}>
        {ADJUSTMENTS.map((k) => (
          <div className="row" key={k}>
            <span style={{ width: 80, color: 'var(--text-2)', textTransform: 'capitalize' }}>{k}</span>
            <input
              type="range"
              min={-100}
              max={100}
              style={{ flex: 1 }}
              onPointerDown={() => app.editor.beginGesture('Adjust image')}
              onPointerUp={() => app.editor.endGesture()}
              onDoubleClick={() => {
                const f = { ...(paint.filters ?? {}) };
                delete f[k];
                onChange({ ...paint, filters: Object.keys(f).length ? f : undefined });
              }}
              value={Math.round((paint.filters?.[k] ?? 0) * 100)}
              onChange={(e) => {
                const f = { ...(paint.filters ?? {}), [k]: Number(e.target.value) / 100 };
                if (!f[k]) delete f[k];
                onChange({ ...paint, filters: Object.keys(f).length ? f : undefined });
              }}
            />
            <span style={{ width: 30, textAlign: 'right', color: 'var(--text-3)' }}>{Math.round((paint.filters?.[k] ?? 0) * 100)}</span>
          </div>
        ))}
      </div>
    )}
    </>
  );
}

function EffectsSection({ nodes }: { nodes: N[] }) {
  const effects = same(nodes, (n) => n.effects);
  const list = effects === 'mixed' ? null : effects;
  const set = (es: Effect[]) => update({ effects: es }, 'Effects');
  return (
    <div className="prop-section">
      <h4>
        <span>Effects</span>
        <button title="Add effect" onClick={() => set([...(list ?? []), { type: 'DROP_SHADOW', color: parseColor('#00000040'), offset: { x: 0, y: 4 }, radius: 4, spread: 0 }])}>
          {Icons.plus}
        </button>
      </h4>
      {list?.map((e, i) => (
        <div key={i} style={{ marginBottom: 8 }}>
          <div className="row">
            <Select
              value={e.type}
              options={[
                { v: 'DROP_SHADOW', label: 'Drop shadow' },
                { v: 'INNER_SHADOW', label: 'Inner shadow' },
                { v: 'LAYER_BLUR', label: 'Layer blur' },
                { v: 'BACKGROUND_BLUR', label: 'Background blur' },
                { v: 'NOISE', label: 'Noise' },
                { v: 'TEXTURE', label: 'Texture' },
                { v: 'GLASS', label: 'Glass' },
                { v: 'SHADER', label: 'Shader' },
              ]}
              onChange={(t) => set(list.map((x, j) => (j === i ? effectOfType(t as Effect['type'], x) : x)))}
            />
            <button className="icon-btn" onClick={() => set(list.map((x, j) => (j === i ? { ...x, visible: x.visible === false ? undefined : false } : x)))}>
              {e.visible === false ? Icons.eyeOff : Icons.eye}
            </button>
            <button className="icon-btn" onClick={() => set(list.filter((_, j) => j !== i))}>
              {Icons.minus}
            </button>
          </div>
          {(e.type === 'DROP_SHADOW' || e.type === 'INNER_SHADOW') && (
            <>
              <div className="row">
                <NumberField label="X" value={e.offset.x} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, offset: { ...e.offset, x: v } } : x)))} />
                <NumberField label="Y" value={e.offset.y} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, offset: { ...e.offset, y: v } } : x)))} />
              </div>
              <div className="row">
                <NumberField label="B" title="Blur" min={0} value={e.radius} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, radius: v } : x)))} />
                <NumberField label="S" title="Spread" value={e.spread ?? 0} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, spread: v } : x)))} />
              </div>
              <div className="paint-row">
                <div className="field">
                  <ColorSwatch color={e.color} onChange={(c) => set(list.map((x, j) => (j === i ? { ...e, color: c } : x)))} />
                  <HexField color={e.color} onChange={(c) => set(list.map((x, j) => (j === i ? { ...e, color: c } : x)))} />
                  <span style={{ color: 'var(--text-2)' }}>{Math.round(e.color.a * 100)}%</span>
                </div>
              </div>
            </>
          )}
          {(e.type === 'LAYER_BLUR' || e.type === 'BACKGROUND_BLUR') && (
            <>
              <div className="row">
                <NumberField label="B" title={e.blurType === 'PROGRESSIVE' ? 'End blur' : 'Blur'} min={0} value={e.radius} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, radius: v } : x)))} />
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--text-2)' }} title="The blur ramps from one strength to another across the layer">
                  <input type="checkbox" checked={e.blurType === 'PROGRESSIVE'} onChange={(ev) => set(list.map((x, j) => (j === i ? { ...e, blurType: ev.target.checked ? 'PROGRESSIVE' : undefined, startRadius: ev.target.checked ? (e.startRadius ?? 0) : undefined } : x)))} /> Progressive
                </label>
              </div>
              {e.blurType === 'PROGRESSIVE' && (
                <div className="row">
                  <NumberField label="From" title="Start blur" min={0} value={e.startRadius ?? 0} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, startRadius: v } : x)))} />
                  <Select
                    value={(() => {
                      const a = e.start ?? { x: 0.5, y: 0 },
                        b = e.end ?? { x: 0.5, y: 1 };
                      return Math.abs(b.y - a.y) >= Math.abs(b.x - a.x) ? (b.y > a.y ? 'down' : 'up') : b.x > a.x ? 'right' : 'left';
                    })()}
                    options={[
                      { v: 'down', label: 'Top → bottom' },
                      { v: 'up', label: 'Bottom → top' },
                      { v: 'right', label: 'Left → right' },
                      { v: 'left', label: 'Right → left' },
                    ]}
                    onChange={(d) => {
                      const ends = { down: [{ x: 0.5, y: 0 }, { x: 0.5, y: 1 }], up: [{ x: 0.5, y: 1 }, { x: 0.5, y: 0 }], right: [{ x: 0, y: 0.5 }, { x: 1, y: 0.5 }], left: [{ x: 1, y: 0.5 }, { x: 0, y: 0.5 }] }[d as 'down'];
                      set(list.map((x, j) => (j === i ? { ...e, start: ends[0], end: ends[1] } : x)));
                    }}
                  />
                </div>
              )}
            </>
          )}
          {e.type === 'NOISE' && (
            <>
              <div className="row">
                <Select
                  value={e.noiseType ?? 'MONOTONE'}
                  options={[
                    { v: 'MONOTONE', label: 'Mono' },
                    { v: 'DUOTONE', label: 'Duo' },
                    { v: 'MULTITONE', label: 'Multi' },
                  ]}
                  onChange={(t) => set(list.map((x, j) => (j === i ? { ...e, noiseType: t as 'MONOTONE', secondaryColor: t === 'DUOTONE' ? (e.secondaryColor ?? parseColor('#ffffff')) : e.secondaryColor } : x)))}
                />
                <NumberField label="Size" title="Grain size" min={0.25} value={e.size ?? 1} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, size: v } : x)))} />
              </div>
              <div className="row">
                <NumberField label="Den" title="Density (0–100%)" min={0} max={100} value={Math.round((e.density ?? 0.5) * 100)} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, density: v / 100 } : x)))} />
                <NumberField label="Op" title="Opacity (0–100%)" min={0} max={100} value={Math.round((e.opacity ?? 0.25) * 100)} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, opacity: v / 100 } : x)))} />
              </div>
              {e.noiseType !== 'MULTITONE' && (
                <div className="paint-row">
                  <div className="field">
                    <ColorSwatch color={e.color ?? parseColor('#000000')} onChange={(c) => set(list.map((x, j) => (j === i ? { ...e, color: c } : x)))} />
                    <HexField color={e.color ?? parseColor('#000000')} onChange={(c) => set(list.map((x, j) => (j === i ? { ...e, color: c } : x)))} />
                    {e.noiseType === 'DUOTONE' && <ColorSwatch color={e.secondaryColor ?? parseColor('#ffffff')} onChange={(c) => set(list.map((x, j) => (j === i ? { ...e, secondaryColor: c } : x)))} />}
                  </div>
                </div>
              )}
            </>
          )}
          {e.type === 'GLASS' && (
            <>
              <div className="row">
                <NumberField label="☀" title="Light intensity (0–100%)" min={0} max={100} value={Math.round((e.lightIntensity ?? 0.5) * 100)} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, lightIntensity: v / 100 } : x)))} />
                <NumberField label="∠" title="Light angle (degrees)" value={e.lightAngle ?? -45} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, lightAngle: v } : x)))} />
              </div>
              <div className="row">
                <NumberField label="Ref" title="Refraction (0–100%)" min={0} max={100} value={Math.round((e.refraction ?? 0.5) * 100)} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, refraction: v / 100 } : x)))} />
                <NumberField label="D" title="Depth (px)" min={1} value={e.depth ?? 6} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, depth: v } : x)))} />
              </div>
              <div className="row">
                <NumberField label="Disp" title="Dispersion (0–100%)" min={0} max={100} value={Math.round((e.dispersion ?? 0.2) * 100)} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, dispersion: v / 100 } : x)))} />
                <NumberField label="B" title="Frost (blur)" min={0} value={e.radius ?? 4} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, radius: v } : x)))} />
              </div>
            </>
          )}
          {e.type === 'SHADER' && <ShaderControls kind="effect" shader={e} slot={nodes.length === 1 ? { nodeId: nodes[0].id, index: i } : undefined} onChange={(s) => set(list.map((x, j) => (j === i ? { ...e, ...s } : x)))} />}
          {e.type === 'TEXTURE' && (
            <div className="row">
              <NumberField label="Size" title="Grain size" min={0.5} value={e.size ?? 4} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, size: v } : x)))} />
              <NumberField label="R" title="How far pixels move" min={0} value={e.radius ?? 4} onChange={(v) => set(list.map((x, j) => (j === i ? { ...e, radius: v } : x)))} />
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--text-2)' }} title="Keep the texture inside the layer's shape">
                <input type="checkbox" checked={e.clipToShape !== false} onChange={(ev) => set(list.map((x, j) => (j === i ? { ...e, clipToShape: ev.target.checked } : x)))} /> Clip
              </label>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * A shader's controls, built from its defineProperties (like Figma's), plus the built-in
 * shaders and a code editor for custom ones (paste a Figma shader's main.ts).
 */
function ShaderControls({ kind, shader, slot, onChange }: { kind: 'fill' | 'effect'; shader: ShaderSource; slot?: { nodeId: NodeId; index: number }; onChange: (s: ShaderSource) => void }) {
  const [props, setProps] = useState<ShaderProperties | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [code, setCode] = useState(shader.source);
  useApp('shaders', () => (slot ? app.shaders.versionOf(slot.nodeId) : 0));
  useEffect(() => {
    let live = true;
    setCode(shader.source);
    app.shaders.properties(shader.source).then(
      (p) => live && (setProps(p), setErr(null)),
      (e: Error) => live && (setProps(null), setErr(e.message)),
    );
    return () => void (live = false);
  }, [shader.source]);
  const presets = SHADER_PRESETS.filter((n) => shaderPresetInfo(n)?.kind === kind);
  const current = presets.find((n) => shaderPreset(n)?.source === shader.source) ?? 'custom';
  const params = shader.params ?? {};
  const setParam = (k: string, v: unknown) => onChange({ ...shader, params: { ...params, [k]: v } });
  const runError = slot ? app.shaders.errorOf(slot.nodeId, kind, slot.index) : null;
  const error = err ?? runError;
  const label = (k: string, d: Record<string, unknown>) => <span style={{ width: 76, flex: 'none', color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(d.description ?? d.label ?? k)}>{String(d.label ?? k)}</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
      <div className="row">
        <Select
          value={current}
          options={[...presets.map((n) => ({ v: n, label: shaderPresetInfo(n)!.name })), { v: 'custom', label: 'Custom code' }]}
          onChange={(v) => {
            if (v === 'custom') return setEditing(true);
            const p = shaderPreset(v)!;
            onChange({ name: p.name, source: p.source });
          }}
        />
        <button className="icon-btn" title="Edit shader code (Figma shader format)" style={{ width: 'auto', padding: '0 6px' }} onClick={() => setEditing((x) => !x)}>
          {'</>'}
        </button>
      </div>
      {!app.shaders.available && <div style={{ color: 'var(--text-2)', fontSize: 11 }}>Shaders need WebGPU, which this browser doesn't have.</div>}
      {error && <div style={{ color: 'var(--danger, #e5484d)', fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{error}</div>}
      {editing && (
        <>
          <textarea
            value={code}
            spellCheck={false}
            placeholder="Paste a Figma shader's main.ts"
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            style={{ fontFamily: 'var(--mono, ui-monospace, monospace)', fontSize: 11, height: 220, resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
          />
          <div className="row">
            <button className="btn" disabled={code === shader.source} onClick={() => onChange({ ...shader, source: code, name: /^\/\/\s*([^:\n]+)/.exec(code)?.[1]?.trim() ?? (current === 'custom' ? shader.name : 'Custom shader') })}>
              Apply code
            </button>
          </div>
        </>
      )}
      {props &&
        Object.entries(props).map(([k, d]) => {
          const v = k in params ? params[k] : d.defaultValue;
          const t = String(d.type);
          if (t === 'number' && d.control === 'select' && Array.isArray(d.options))
            return (
              <div className="row" key={k}>
                {label(k, d)}
                <Select value={String(v)} options={(d.options as { value: number; label: string }[]).map((o) => ({ v: String(o.value), label: o.label }))} onChange={(x) => setParam(k, Number(x))} />
              </div>
            );
          if (t === 'number')
            return (
              <div className="row" key={k}>
                {label(k, d)}
                <NumberField value={Number(v) || 0} min={d.min as number | undefined} max={d.max as number | undefined} step={(d.step as number | undefined) ?? 1} suffix={d.unit as string | undefined} onChange={(x) => setParam(k, x)} />
              </div>
            );
          if (t === 'boolean')
            return (
              <label className="row" key={k} style={{ gap: 6, alignItems: 'center' }}>
                {label(k, d)}
                <input type="checkbox" checked={!!v} onChange={(e) => setParam(k, e.target.checked)} />
              </label>
            );
          if (t === 'color') {
            const c = (v ?? { r: 0, g: 0, b: 0, a: 1 }) as Color;
            return (
              <div className="row" key={k}>
                {label(k, d)}
                <div className="field">
                  <ColorSwatch color={c} onChange={(x) => setParam(k, x)} />
                  <HexField color={c} onChange={(x) => setParam(k, x)} />
                </div>
              </div>
            );
          }
          if (t === 'string')
            return (
              <div className="row" key={k}>
                {label(k, d)}
                <input value={String(v ?? '')} onChange={(e) => setParam(k, e.target.value)} onKeyDown={(e) => e.stopPropagation()} style={{ flex: 1, minWidth: 0 }} />
              </div>
            );
          if (t === 'gradient') {
            const g = (v ?? { stops: [] }) as { stops: { position: number; color: Color }[] };
            return (
              <div className="row" key={k}>
                {label(k, d)}
                <div className="field">
                  {g.stops.slice(0, 8).map((s, i) => (
                    <ColorSwatch key={i} color={s.color} onChange={(x) => setParam(k, { ...g, stops: g.stops.map((y, j) => (j === i ? { ...y, color: x } : y)) })} />
                  ))}
                </div>
              </div>
            );
          }
          // points and their radius/angle variants: one field per number in the value
          if (v && typeof v === 'object') {
            const o = v as Record<string, unknown>;
            const nums = Object.keys(o).filter((f) => typeof o[f] === 'number');
            return (
              <div className="row" key={k} style={{ flexWrap: 'wrap' }}>
                {label(k, d)}
                {nums.map((f) => (
                  <NumberField key={f} label={f === 'radius' ? 'r' : f === 'angle' ? '∠' : f} value={o[f] as number} onChange={(x) => setParam(k, { ...o, [f]: x })} width={52} />
                ))}
              </div>
            );
          }
          return null;
        })}
    </div>
  );
}

function StickySection({ nodes }: { nodes: N[] }) {
  return (
    <div className="prop-section">
      <h4>Sticky</h4>
      <div className="row" style={{ flexWrap: 'wrap' }}>
        {Object.entries(STICKY_COLORS).map(([name, c]) => (
          <button key={name} title={name} className="sticky-swatch" style={{ background: c, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.08)' }} onClick={() => update({ fills: [{ type: 'SOLID', color: parseColor(c) }] }, 'Sticky color')} />
        ))}
      </div>
      <div className="row">
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--text-2)' }}>
          <input type="checkbox" checked={nodes.every((n) => !!n.authorName)} onChange={(e) => update({ authorName: e.target.checked ? app.userName : undefined }, 'Show author')} /> Show author
        </label>
      </div>
    </div>
  );
}

function ShapeSection({ nodes }: { nodes: N[] }) {
  const kinds = ['SQUARE', 'ELLIPSE', 'ROUNDED_RECTANGLE', 'DIAMOND', 'TRIANGLE_UP', 'TRIANGLE_DOWN', 'PARALLELOGRAM', 'HEXAGON', 'STAR', 'CYLINDER', 'ARROW_RIGHT', 'ARROW_LEFT'] as const;
  return (
    <div className="prop-section">
      <h4>Shape</h4>
      <div className="row">
        <Select value={same(nodes, (n) => n.shape ?? 'SQUARE')} options={kinds.map((k) => ({ v: k, label: k.charAt(0) + k.slice(1).toLowerCase().replace(/_/g, ' ') }))} onChange={(v) => update({ shape: v }, 'Shape')} />
      </div>
    </div>
  );
}

function ConnectorSection({ nodes }: { nodes: N[] }) {
  return (
    <div className="prop-section">
      <h4>Connector</h4>
      <div className="row">
        <Seg
          value={same(nodes, (n) => n.connectorType ?? 'ELBOW')}
          options={[
            { v: 'ELBOW', label: 'Elbow' },
            { v: 'STRAIGHT', label: 'Straight' },
            { v: 'CURVED', label: 'Curved' },
          ]}
          onChange={(v) => update({ connectorType: v }, 'Connector type')}
        />
      </div>
      <div className="row">
        <CapSelect nodes={nodes} which="startCap" />
        <CapSelect nodes={nodes} which="endCap" />
      </div>
    </div>
  );
}

function ExportSection({ nodes }: { nodes: N[] }) {
  const ids = nodes.length ? nodes.map((n) => n.id) : app.doc.children(app.editor.pageId).map((n) => n.id);
  const [scale, setScale] = useState(2);
  const name = nodes.length === 1 ? nodes[0].name : app.doc.get(app.editor.pageId)?.name ?? 'export';
  const png = async () => {
    const b = exportBounds(app.doc, ids);
    if (!b) return;
    const c = document.createElement('canvas');
    c.width = Math.ceil(b.width * scale);
    c.height = Math.ceil(b.height * scale);
    const ctx = c.getContext('2d')!;
    const r = new Renderer2D(app.doc);
    for (const id of ids) r.renderSubtree(ctx, id, [scale, 0, 0, scale, -b.x * scale, -b.y * scale], { images: app.images, shaders: app.shaders });
    c.toBlob((blob) => blob && download(blob, `${name}@${scale}x.png`));
  };
  const svg = () => download(new Blob([toSvg(app.doc, ids, { shaderImage: (id, k, i) => app.shaders.dataUrl(id, k, i) })], { type: 'image/svg+xml' }), `${name}.svg`);
  return (
    <div className="prop-section keep-enabled">
      <h4>Export</h4>
      <div className="row">
        <Select value={String(scale)} options={['1', '2', '3', '4'].map((v) => ({ v, label: `${v}x` }))} onChange={(v) => setScale(Number(v))} />
        <button className="btn secondary" onClick={png}>
          PNG
        </button>
        <button className="btn secondary" onClick={svg}>
          SVG
        </button>
        <a className="btn secondary" style={{ textDecoration: 'none' }} title="Vector PDF, one page per frame" href={`/api/files/${app.fileId}/pdf?page=${app.editor.pageId}${nodes.length ? `&ids=${ids.join(',')}` : ''}`} download>
          PDF
        </a>
      </div>
    </div>
  );
}

function CodeSection({ nodes }: { nodes: N[] }) {
  const [format, setFormat] = useState<'css' | 'html' | 'react' | 'swiftui' | 'compose'>('css');
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ids = nodes.map((n) => n.id);
  let code = '';
  if (open) {
    try {
      code = generateCode(app.doc, ids, { format });
    } catch (e) {
      code = String(e);
    }
  }
  return (
    <div className="prop-section keep-enabled">
      <h4>
        <span style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
          {open ? '▾' : '▸'} Code
        </span>
        {open && (
          <button
            title="Copy code"
            onClick={() => {
              void navigator.clipboard.writeText(code);
              setCopied(true);
              setTimeout(() => setCopied(false), 1000);
            }}
          >
            {copied ? '✓' : '⧉'}
          </button>
        )}
      </h4>
      {open && (
        <>
          <div className="row">
            <Seg
              value={format}
              options={[
                { v: 'css', label: 'CSS' },
                { v: 'html', label: 'HTML' },
                { v: 'react', label: 'React' },
                { v: 'swiftui', label: 'SwiftUI' },
                { v: 'compose', label: 'Compose' },
              ]}
              onChange={setFormat}
            />
          </div>
          <pre className="code-view">{code}</pre>
        </>
      )}
    </div>
  );
}

function download(blob: Blob, filename: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export function useEffectOnce(fn: () => void) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fn, []);
}

/** Per-side stroke weights (top, right, bottom, left) for rectangles and frames. */
function SideStrokes({ node }: { node: NodeData }) {
  const [open, setOpen] = useState(!!node.strokeWeights);
  const w = node.strokeWeights ?? [node.strokeWeight, node.strokeWeight, node.strokeWeight, node.strokeWeight];
  const set = (i: number, v: number) => {
    const next = w.map((x, j) => (j === i ? v : x)) as [number, number, number, number];
    app.editor.update(node.id, { strokeWeights: next, strokeWeight: Math.max(...next) }, 'Stroke sides');
  };
  return (
    <>
      <div className="row">
        <button className={`btn secondary ${open ? 'on' : ''}`} title="Set each side's weight" onClick={() => setOpen(!open)}>
          ⊞ Individual sides
        </button>
      </div>
      {open && (
        <div className="row">
          {(['T', 'R', 'B', 'L'] as const).map((side, i) => (
            <NumberField key={side} label={side} title={['Top', 'Right', 'Bottom', 'Left'][i]} min={0} value={w[i]} onChange={(v) => set(i, v)} />
          ))}
        </div>
      )}
    </>
  );
}

const BLEND_OPTIONS = ['NORMAL', 'DARKEN', 'MULTIPLY', 'LINEAR_BURN', 'COLOR_BURN', 'LIGHTEN', 'SCREEN', 'LINEAR_DODGE', 'COLOR_DODGE', 'OVERLAY', 'SOFT_LIGHT', 'HARD_LIGHT', 'DIFFERENCE', 'EXCLUSION', 'HUE', 'SATURATION', 'COLOR', 'LUMINOSITY'];
const isContainerType = (t: string) => t === 'FRAME' || t === 'GROUP' || t === 'COMPONENT' || t === 'INSTANCE' || t === 'SECTION';

/** Plugins that left "relaunch" buttons on this layer (figma setRelaunchData). */
function RelaunchButtons({ node }: { node: N }) {
  const plugins = installedPlugins();
  const buttons = Object.entries(node.relaunchData ?? {}).flatMap(([pid, cmds]) => {
    const def = plugins.find((p) => p.id === pid);
    return def ? Object.entries(cmds).map(([cmd, label]) => ({ def, cmd, label: label || def.name })) : [];
  });
  if (!buttons.length) return null;
  return (
    <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
      {buttons.map((b) => (
        <button key={b.def.id + b.cmd} className="btn secondary" title={b.def.name} onClick={() => runPlugin(b.def, b.cmd)}>
          ↻ {b.label}
        </button>
      ))}
    </div>
  );
}
